import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import {
  Plus,
  Pencil,
  Trash,
  Check,
  X,
  List,
  ChevronDown,
  ChevronRight,
  Filter,
  Edit,
  CheckSquare,
  Square,
  Eye,
  EyeOff,
  Clock,
  Brain,
  Calendar,
  Link,
  AlertTriangle,
  Target,
  Zap
} from 'lucide-react';

// Streamlined inline-editable template row component
function TemplateRow({ template, category, phase, onUpdate, onDelete, onEdit, templates }) {
  const [editing, setEditing] = useState({});
  const [localValues, setLocalValues] = useState({});

  const handleInlineEdit = (field, value) => {
    setLocalValues(prev => ({ ...prev, [field]: value }));
  };

  const saveField = async (field, value) => {
    try {
      await onUpdate(template.id, { [field]: value });
      setEditing(prev => ({ ...prev, [field]: false }));
      setLocalValues(prev => ({ ...prev, [field]: undefined }));
    } catch (error) {
      console.error('Error saving field:', error);
      alert('Failed to save changes');
    }
  };

  const startEdit = (field) => {
    setEditing(prev => ({ ...prev, [field]: true }));
    setLocalValues(prev => ({ ...prev, [field]: template[field] }));
  };

  const cancelEdit = (field) => {
    setEditing(prev => ({ ...prev, [field]: false }));
    setLocalValues(prev => ({ ...prev, [field]: undefined }));
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'critical': return 'text-red-600 bg-red-50';
      case 'high': return 'text-orange-600 bg-orange-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'low': return 'text-emerald-600 bg-emerald-50';
      default: return 'text-slate-600 bg-stone-50';
    }
  };

  const getIntensityColor = (level) => {
    switch(level) {
      case 'mandatory': return 'text-red-600 bg-red-50';
      case 'high': return 'text-orange-600 bg-orange-50';
      case 'medium': return 'text-azure-600 bg-azure-50';
      case 'low': return 'text-emerald-600 bg-emerald-50';
      case 'optional': return 'text-slate-600 bg-stone-50';
      default: return 'text-slate-600 bg-stone-50';
    }
  };

  // Helper function to format duration for display
  const formatDuration = (minutes) => {
    if (!minutes) return '1h';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h${mins}m`;
  };

  // Helper function to parse duration input
  const parseDuration = (input) => {
    if (!input) return 60;
    const match = input.toLowerCase().match(/^(\d+(?:\.\d+)?)\s*([hm]?)$/);
    if (match) {
      const value = parseFloat(match[1]);
      const unit = match[2] || 'h'; // default to hours
      return unit === 'h' ? Math.round(value * 60) : Math.round(value);
    }
    return 60; // fallback
  };

  // Dropdown options
  const intensityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' }
  ];

  return (
    <tr className={`hover:bg-stone-50 ${!template.is_active ? 'opacity-50' : ''}`}>
      {/* Template Info */}
      <td className="px-4 py-3">
        <div className="space-y-1">
          <div className="font-medium text-slate-900">{template.title}</div>
          <div className="text-xs text-stone-500">
            {category} • {phase} • {template.anchor_id}
          </div>
          {template.description && (
            <div className="text-xs text-slate-600 max-w-xs truncate">
              {template.description}
            </div>
          )}
        </div>
      </td>

      {/* Timing - Inline Editable */}
      <td className="px-4 py-3">
        <div className="space-y-1">
          {/* Legacy Offset */}
          <div className="flex items-center gap-2 text-sm">
            {editing.offset_days ? (
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  value={localValues.offset_days || ''}
                  onChange={(e) => handleInlineEdit('offset_days', parseInt(e.target.value) || 0)}
                  onBlur={() => saveField('offset_days', localValues.offset_days)}
                  onKeyPress={(e) => e.key === 'Enter' && saveField('offset_days', localValues.offset_days)}
                  className="w-16 px-1 py-0.5 text-xs border rounded"
                  autoFocus
                />
                <button onClick={() => cancelEdit('offset_days')} className="text-xs text-stone-400 hover:text-slate-600">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => startEdit('offset_days')}
                className="text-xs text-azure-600 hover:text-azure-800 underline"
              >
                {template.offset_days >= 0 ? '+' : ''}{template.offset_days || 7}d
              </button>
            )}
          </div>
          
          {/* Interpolation Bounds Preview */}
          {(template.max_rd_offset_days || template.max_ed_offset_days) && (
            <div className="text-xs text-stone-500">
              {template.max_rd_offset_days && `RD: ${template.max_rd_offset_days}d`}
              {template.max_ed_offset_days && ` ED: ${template.max_ed_offset_days}d`}
            </div>
          )}
        </div>
      </td>

      {/* Enhanced Inline Fields */}
      <td className="px-4 py-3">
        <div className="space-y-2">
          {/* Duration - Inline Editable */}
          <div className="flex items-center gap-2">
            <Clock className="h-3 w-3 text-stone-400" />
            {editing.estimated_duration ? (
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  value={localValues.estimated_duration || ''}
                  onChange={(e) => handleInlineEdit('estimated_duration', e.target.value)}
                  onBlur={() => saveField('estimated_duration', parseDuration(localValues.estimated_duration))}
                  onKeyPress={(e) => e.key === 'Enter' && saveField('estimated_duration', parseDuration(localValues.estimated_duration))}
                  className="w-12 px-1 py-0.5 text-xs border rounded"
                  placeholder="1h"
                  autoFocus
                />
                <button onClick={() => cancelEdit('estimated_duration')} className="text-xs text-stone-400 hover:text-slate-600">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => startEdit('estimated_duration')}
                className="text-xs text-azure-600 hover:text-azure-800 underline"
              >
                {formatDuration(template.estimated_duration)}
              </button>
            )}
          </div>

          {/* Cognitive Intensity - Inline Editable */}
          <div className="flex items-center gap-2">
            <Brain className="h-3 w-3 text-stone-400" />
            {editing.cognitive_intensity ? (
              <div className="flex items-center gap-1">
                <select
                  value={localValues.cognitive_intensity || template.cognitive_intensity || 'medium'}
                  onChange={(e) => handleInlineEdit('cognitive_intensity', e.target.value)}
                  onBlur={() => saveField('cognitive_intensity', localValues.cognitive_intensity)}
                  className="w-20 px-1 py-0.5 text-xs border rounded"
                  autoFocus
                >
                  {intensityOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <button onClick={() => cancelEdit('cognitive_intensity')} className="text-xs text-stone-400 hover:text-slate-600">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => startEdit('cognitive_intensity')}
                className="text-xs text-azure-600 hover:text-azure-800 underline"
              >
                {template.cognitive_intensity || 'medium'}
              </button>
            )}
          </div>

          {/* Timing Sensitivity - Inline Editable */}
          <div className="flex items-center gap-2">
            <Calendar className="h-3 w-3 text-stone-400" />
            {editing.timing_sensitivity ? (
              <div className="flex items-center gap-1">
                <select
                  value={localValues.timing_sensitivity || template.timing_sensitivity || 'medium'}
                  onChange={(e) => handleInlineEdit('timing_sensitivity', e.target.value)}
                  onBlur={() => saveField('timing_sensitivity', localValues.timing_sensitivity)}
                  className="w-20 px-1 py-0.5 text-xs border rounded"
                  autoFocus
                >
                  {intensityOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <button onClick={() => cancelEdit('timing_sensitivity')} className="text-xs text-stone-400 hover:text-slate-600">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => startEdit('timing_sensitivity')}
                className="text-xs text-azure-600 hover:text-azure-800 underline"
              >
                {template.timing_sensitivity || 'medium'}
              </button>
            )}
          </div>

          {/* Module Link - Inline Editable */}
          <div className="flex items-center gap-2">
            <Link className="h-3 w-3 text-stone-400" />
            {editing.module_link ? (
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  value={localValues.module_link || ''}
                  onChange={(e) => handleInlineEdit('module_link', e.target.value)}
                  onBlur={() => saveField('module_link', localValues.module_link || null)}
                  onKeyPress={(e) => e.key === 'Enter' && saveField('module_link', localValues.module_link || null)}
                  className="w-24 px-1 py-0.5 text-xs border rounded"
                  placeholder="/path"
                  autoFocus
                />
                <button onClick={() => cancelEdit('module_link')} className="text-xs text-stone-400 hover:text-slate-600">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => startEdit('module_link')}
                className="text-xs text-azure-600 hover:text-azure-800 underline truncate max-w-20"
                title={template.module_link || 'No link set'}
              >
                {template.module_link || 'none'}
              </button>
            )}
          </div>

          {/* Dependency - Inline Editable */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-stone-400">🔗</span>
            {editing.depends_on_template_id ? (
              <div className="flex items-center gap-1">
                <select
                  value={localValues.depends_on_template_id || ''}
                  onChange={(e) => handleInlineEdit('depends_on_template_id', e.target.value || null)}
                  onBlur={() => saveField('depends_on_template_id', localValues.depends_on_template_id)}
                  className="w-32 px-1 py-0.5 text-xs border rounded"
                  autoFocus
                >
                  <option value="">No dependency</option>
                  {templates
                    .filter(t => t.id !== template.id)
                    .map(t => (
                      <option key={t.id} value={t.id}>
                        {t.title}
                      </option>
                    ))}
                  
                </select>
                <button onClick={() => cancelEdit('depends_on_template_id')} className="text-xs text-stone-400 hover:text-slate-600">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => startEdit('depends_on_template_id')}
                className="text-xs text-azure-600 hover:text-azure-800 underline truncate max-w-24"
                title={templates.find(t => t.id === template.depends_on_template_id)?.title || 'No dependency'}
              >
                {templates.find(t => t.id === template.depends_on_template_id)?.title || 'none'}
              </button>
            )}
          </div>
        </div>
      </td>

      {/* Crisis Support Fields */}
      <td className="px-4 py-3">
        <div className="space-y-2">
          {/* Incompletion Cost - Inline Editable */}
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-3 w-3 text-stone-400" />
            {editing.incompletion_cost ? (
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  value={localValues.incompletion_cost !== undefined ? localValues.incompletion_cost : ''}
                  onChange={(e) => handleInlineEdit('incompletion_cost', parseFloat(e.target.value))}
                  onBlur={() => saveField('incompletion_cost', localValues.incompletion_cost)}
                  onKeyPress={(e) => e.key === 'Enter' && saveField('incompletion_cost', localValues.incompletion_cost)}
                  className="w-12 px-1 py-0.5 text-xs border rounded"
                  placeholder="0.9"
                  autoFocus
                />
                <button onClick={() => cancelEdit('incompletion_cost')} className="text-xs text-stone-400 hover:text-slate-600">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => startEdit('incompletion_cost')}
                className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                  template.incompletion_cost >= 0.9 ? 'bg-coral-100 text-coral-900' :
                  template.incompletion_cost >= 0.7 ? 'bg-orange-100 text-orange-900' :
                  template.incompletion_cost >= 0.5 ? 'bg-yellow-100 text-yellow-900' :
                  template.incompletion_cost ? 'bg-stone-100 text-stone-600' :
                  'text-stone-400 hover:text-azure-800 underline'
                }`}
                title="Consequence severity (0.0-1.0)"
              >
                {template.incompletion_cost ? `Cost: ${template.incompletion_cost.toFixed(2)}` : 'Set cost'}
              </button>
            )}
          </div>

          {/* MVV Risk - Inline Editable */}
          <div className="flex items-center gap-2">
            <Target className="h-3 w-3 text-stone-400" />
            {editing.mvv_risk ? (
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  value={localValues.mvv_risk !== undefined ? localValues.mvv_risk : ''}
                  onChange={(e) => handleInlineEdit('mvv_risk', parseFloat(e.target.value))}
                  onBlur={() => saveField('mvv_risk', localValues.mvv_risk)}
                  onKeyPress={(e) => e.key === 'Enter' && saveField('mvv_risk', localValues.mvv_risk)}
                  className="w-12 px-1 py-0.5 text-xs border rounded"
                  placeholder="0.7"
                  autoFocus
                />
                <button onClick={() => cancelEdit('mvv_risk')} className="text-xs text-stone-400 hover:text-slate-600">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => startEdit('mvv_risk')}
                className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                  template.mvv_risk >= 0.7 ? 'bg-coral-100 text-coral-900' :
                  template.mvv_risk >= 0.4 ? 'bg-yellow-100 text-yellow-900' :
                  template.mvv_risk < 0.4 && template.mvv_risk !== null && template.mvv_risk !== undefined ? 'bg-teal-100 text-teal-900' :
                  template.mvv_risk ? 'bg-stone-100 text-stone-600' :
                  'text-stone-400 hover:text-azure-800 underline'
                }`}
                title="Quality gap (0.0-1.0)"
              >
                {template.mvv_risk !== null && template.mvv_risk !== undefined ? `Risk: ${template.mvv_risk.toFixed(2)}` : 'Set risk'}
              </button>
            )}
          </div>

          {/* Time Estimates */}
          <div className="flex items-center gap-2 text-xs">
            <Zap className="h-3 w-3 text-stone-400" />
            {editing.mvv_time_required ? (
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  value={localValues.mvv_time_required || ''}
                  onChange={(e) => handleInlineEdit('mvv_time_required', e.target.value)}
                  onBlur={() => saveField('mvv_time_required', parseDuration(localValues.mvv_time_required))}
                  onKeyPress={(e) => e.key === 'Enter' && saveField('mvv_time_required', parseDuration(localValues.mvv_time_required))}
                  className="w-12 px-1 py-0.5 text-xs border rounded"
                  placeholder="30m"
                  autoFocus
                />
                <button onClick={() => cancelEdit('mvv_time_required')} className="text-xs text-stone-400 hover:text-slate-600">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => startEdit('mvv_time_required')}
                className="text-xs text-azure-600 hover:text-azure-800 underline"
                title="MVV time estimate"
              >
                {template.mvv_time_required ? `MVV: ${formatDuration(template.mvv_time_required)}` : 'MVV time'}
              </button>
            )}
            <span className="text-stone-400">/</span>
            {editing.optimal_time_required ? (
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  value={localValues.optimal_time_required || ''}
                  onChange={(e) => handleInlineEdit('optimal_time_required', e.target.value)}
                  onBlur={() => saveField('optimal_time_required', parseDuration(localValues.optimal_time_required))}
                  onKeyPress={(e) => e.key === 'Enter' && saveField('optimal_time_required', parseDuration(localValues.optimal_time_required))}
                  className="w-12 px-1 py-0.5 text-xs border rounded"
                  placeholder="60m"
                  autoFocus
                />
                <button onClick={() => cancelEdit('optimal_time_required')} className="text-xs text-stone-400 hover:text-slate-600">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => startEdit('optimal_time_required')}
                className="text-xs text-azure-600 hover:text-azure-800 underline"
                title="Optimal time estimate"
              >
                {template.optimal_time_required ? `Opt: ${formatDuration(template.optimal_time_required)}` : 'Opt time'}
              </button>
            )}
          </div>

          {/* AI Generation - Inline Editable */}
          <div className="flex items-center gap-2 text-xs">
            <span className="text-stone-400">🤖</span>
            {editing.can_autogenerate ? (
              <div className="flex items-center gap-1">
                <select
                  value={localValues.can_autogenerate || template.can_autogenerate || ''}
                  onChange={(e) => handleInlineEdit('can_autogenerate', e.target.value || null)}
                  onBlur={() => saveField('can_autogenerate', localValues.can_autogenerate)}
                  className="w-20 px-1 py-0.5 text-xs border rounded"
                  autoFocus
                >
                  <option value="">Not set</option>
                  <option value="yes">Yes</option>
                  <option value="partial">Partial</option>
                  <option value="no">No</option>
                </select>
                <button onClick={() => cancelEdit('can_autogenerate')} className="text-xs text-stone-400 hover:text-slate-600">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => startEdit('can_autogenerate')}
                className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                  template.can_autogenerate === 'yes' ? 'bg-emerald-100 text-emerald-900' :
                  template.can_autogenerate === 'partial' ? 'bg-yellow-100 text-yellow-900' :
                  template.can_autogenerate === 'no' ? 'bg-stone-100 text-stone-600' :
                  'text-stone-400 hover:text-azure-800 underline'
                }`}
                title="Can AI autogenerate?"
              >
                {template.can_autogenerate ? `AI: ${template.can_autogenerate}` : 'Set AI'}
              </button>
            )}
          </div>
        </div>
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        <button
          onClick={() => onUpdate(template.id, { is_active: !template.is_active })}
          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
            template.is_active 
              ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200' 
              : 'bg-red-100 text-red-800 hover:bg-red-200'
          }`}
        >
          {template.is_active ? 'Active' : 'Inactive'}
        </button>
      </td>

      {/* Actions */}
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={() => onEdit(template)}
            className="p-1 text-stone-400 hover:text-azure-600"
            title="Edit template"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            onClick={() => onDelete(template.id)}
            className="p-1 text-stone-400 hover:text-red-600"
            title="Delete template"
          >
            <Trash className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function MilestoneTemplateAdmin() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [activeFormTab, setActiveFormTab] = useState('basic'); // basic, workflow, mvv, timing, advanced
  const [showCompletionRuleModal, setShowCompletionRuleModal] = useState(false);
  const [completionRuleData, setCompletionRuleData] = useState({
    rule_name: '',
    watch_table: 'timeline_items',
    watch_row_id: '',
    watch_row_label: '',
    watch_column: '',
    operator: '=',
    value: '',
    description: ''
  });
  const [activeView, setActiveView] = useState('table'); // 'table' or 'tree'
  const [showWorkflowEditor, setShowWorkflowEditor] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [groupBy, setGroupBy] = useState('category'); // 'category' or 'workflow'
  const [expandedTemplates, setExpandedTemplates] = useState(new Set());
  const [showCompatibilityFilters, setShowCompatibilityFilters] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [showBatchUpdate, setShowBatchUpdate] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchUpdateData, setBatchUpdateData] = useState({});
  const [inlineEditField, setInlineEditField] = useState(null); // { templateId, field }
  const [sortBy, setSortBy] = useState('category'); // category, due_date, priority, anchor
  const [sortDirection, setSortDirection] = useState('asc');
  const [filters, setFilters] = useState({
    category: '',
    phase: '',
    intensity: '',
    priority: '',
    generation_timing: '',
    is_active: 'all',
    workload_capacity: '',
    disclosure_pace: '',
    timeline_pressure: '',
    estimated_hours_range: ''
  });
  const [subtasks, setSubtasks] = useState({});
  const [showSubtaskForm, setShowSubtaskForm] = useState(null);
  const [subtaskFormData, setSubtaskFormData] = useState({
    title: '',
    description: '',
    offset_from_parent: 0,
    completion_order: 0,
    is_optional: false
  });
  const [formData, setFormData] = useState({
    category: '',
    phase: '',
    title: '',
    description: '',
    anchor_id: 'timeline_baseline',
    // Single signed offset (positive = after anchor, negative = before)
    offset_days: 7,
    // New interpolation bounds system (signed offsets)
    max_rd_offset_days: null,
    min_rd_offset_days: null,
    max_ed_offset_days: null,
    min_ed_offset_days: null,
    // Target date fields for reference
    max_rd_target_date: null,
    min_rd_target_date: null,
    max_ed_target_date: null,
    min_ed_target_date: null,
    // New 3-field intensity system
    importance_level: 'medium',
    cognitive_intensity: 'medium', 
    timing_sensitivity: 'medium',
    // Legacy intensity (deprecated but kept for compatibility)
    intensity_level: 'standard',
    // Core fields
    priority: 'medium',
    is_active: true,
    is_college_specific: false,
    tier_level: 'tier1',
    generation_timing: 'with_deadlines',
    estimated_duration: 60,
    capacity_weight: 1,
    batch_group: '',
    // Dependencies
    depends_on_template_id: null,
    dependency_type: 'sequential',
    dependency_constraints: {},
    depends_on_template_ids: [],
    prerequisite_tasks: [],
    // Timing modifiers
    timeline_pressure_modifier: {},
    application_round_modifier: {},
    business_days_only: false,
    avoid_weekends: true,
    minimum_lead_days: 7,
    ed_priority_bump_days: 0,
    // Resources and navigation
    module_name: '',
    module_link: '',
    action_route: '',
    action_label: '',
    resource_urls: [],
    // Additional schema fields
    epic_id: '',
    native_pressure: 'comprehensive',
    show_in_min_views: true,
    // College visibility fields (for conditional visibility system)
    show_for_ed_schools: true,
    show_for_ea_schools: true,
    show_for_rd_schools: true,
    show_for_waitlisted_schools: false,
    show_for_accepted_schools: true,
    show_for_selected_school: false,
    // Fallback anchor system
    fallback_anchor_id: null,
    fallback_offset_days: null,
    // Workflow organization
    workflow_key: '',
    sub_workflow_key: '',
    // MVV (Minimum Viable Version) fields
    min_viable_version: '',
    mvv_time_required: null,
    mvv_risk: null,
    // Optimal version fields
    optimal_version: '',
    optimal_time_required: null,
    incompletion_cost: null,
    // Completion rule
    completion_rule: '',
    // Metadata for storing additional configuration
    metadata: {}
  });

  const categories = ['application', 'essay', 'testing', 'financial_aid', 'profile', 'research', 'enrollment'];
  const phases = ['exploration', 'preparation', 'application', 'decision'];
  const phaseOrder = { 'exploration': 1, 'preparation': 2, 'application': 3, 'decision': 4 };
  const priorities = ['mandatory', 'critical', 'high', 'medium', 'low'];
  const intensityLevels = ['crisis', 'standard', 'comprehensive']; // Legacy - deprecated
  const epics = ['application', 'essay', 'testing', 'financial_aid', 'profile', 'research'];
  
  // New 3-field intensity system
  const importanceLevels = [
    { value: 'mandatory', label: 'Mandatory', description: 'Must be completed' },
    { value: 'high', label: 'High', description: 'Very important' },
    { value: 'medium', label: 'Medium', description: 'Moderately important' },
    { value: 'low', label: 'Low', description: 'Nice to have' },
    { value: 'optional', label: 'Optional', description: 'Can be skipped' }
  ];
  
  const cognitiveIntensityLevels = [
    { value: 'high', label: 'High', description: 'Complex thinking, research, writing' },
    { value: 'medium', label: 'Medium', description: 'Some planning and decision-making' },
    { value: 'low', label: 'Low', description: 'Simple administrative tasks' }
  ];
  
  const timingSensitivityLevels = [
    { value: 'high', label: 'High', description: 'Time-critical, deadline-sensitive' },
    { value: 'medium', label: 'Medium', description: 'Somewhat time-dependent' },
    { value: 'low', label: 'Low', description: 'Flexible timing' }
  ];
  
  // Helper function to display priority (handles both string and numeric formats)
  const formatPriority = (priority) => {
    if (typeof priority === 'string') return priority;
    if (typeof priority === 'number') {
      const priorityMap = { 1: 'low', 2: 'medium', 3: 'high', 4: 'critical', 5: 'mandatory' };
      return priorityMap[priority] || priority;
    }
    return priority;
  };
  
  // New tier and timing options
  const tierLevels = [
    { value: 'global', label: 'Global (once per student)' },
    { value: 'tier1', label: 'Tier 1 Universal (core college tasks)' },
    { value: 'tier2', label: 'Tier 2 Standard (enhanced college tasks)' },
    { value: 'tier3', label: 'Tier 3 Conditional (situational tasks)' }
  ];
  
  const generationTimings = [
    { value: 'with_global', label: 'With Global Milestones' },
    { value: 'immediate', label: 'Immediate (when college added)' },
    { value: 'with_deadlines', label: 'With Deadlines (manual generation)' }
  ];

  const workloadCapacityOptions = [
    { value: 'light', label: 'Light (2-4 hrs/week compatible)' },
    { value: 'moderate', label: 'Moderate (5-8 hrs/week compatible)' },
    { value: 'intensive', label: 'Intensive (9+ hrs/week compatible)' }
  ];

  // Workflow organization structure
  const workflowStructure = {
    'build_strategic_profile': {
      label: 'Build Strategic Profile',
      subWorkflows: {
        'complete_strategic_assessment': 'Complete Strategic Assessment',
        'profile_preparation': 'Profile Preparation',
        'complete_academic_profile': 'Complete Academic Profile',
        'complete_all_testing': 'Complete All Testing',
      }
    },
    'build_college_list': {
      label: 'Build College List',
      subWorkflows: {
        'initial_college_exploration': 'Initial College Exploration',
        'demonstrate_college_interest': 'Demonstrate College Interest',
        'categorize_colleges': 'Categorize Colleges',
      }
    },
    'secure_recommendations': {
      label: 'Secure Strong Recommendations',
      subWorkflows: {
        'recommendations_foundation': 'Recommendations Foundation',
        'triggered_teacher_recommendation': 'Teacher Recommendation',
        'triggered_counselor_recommendation': 'Counselor Recommendation',
        'triggered_additional_recommendations': 'Additional Recommendations',
      }
    },
    'write_personal_statement': {
      label: 'Write Personal Statement',
      subWorkflows: {
        'discover_your_story': 'Discover Your Story',
        'draft_your_essay': 'Draft PersonalEssay',
        'get_feedback_and_revise': 'Get Feedback and Revise',
        'finalize_personal_essay': 'Finalize Personal Essay'
      }
    },
    'setup_application_accounts': {
      label: 'Set Up Application Accounts',
      subWorkflows: {
        'common_app_setup': 'Common App Setup',
        'financial_aid_setup': 'Financial Aid Setup',
        }
    },
    'research_scholarships': {
      label: 'Research & Apply for Scholarships',
      subWorkflows: {
        'scholarship_discovery': 'Scholarship Discovery',
        'apply_for_scholarship': 'Apply for Scholarship',
      }
    },
    'complete_college_application': {
      label: 'Complete College Application',
      subWorkflows: {
        'strategic_research': 'Strategic Research',
        'triggered_supplemental_essay': 'Write Supplemental Essay',
        'triggered_complete_application_requirements': 'Complete Application Requirements',
        'submit_application': 'Submit Application',
      }
    },
    
    'make_final_decision': {
      label: 'Make Final Decision',
      subWorkflows: {
        'compare_offers': 'Compare Offers',
        'appeal_aid_offers': 'Appeal Aid Offers',
        'waitlist_strategy': 'Create Waitlist Strategy',
        'make_decision': 'Make Final Decision',
      }
    },
    'prepare_for_enrollment': {
      label: 'Prepare for Enrollment',
      subWorkflows: {
        'academic_preparation': 'Academic Preparation',
        'enrollment_requirements': 'Enrollment Requirements',
        'get_ready_for_college': 'Get Ready for College',
    }
  }
  };
       

  const disclosurePaceOptions = [
    { value: 'conservative', label: 'Conservative (essential tasks only)' },
    { value: 'balanced', label: 'Balanced (recommended + essential)' },
    { value: 'aggressive', label: 'Aggressive (all enhancement tasks)' }
  ];

  const timelinePressureOptions = [
    { value: 'crisis', label: 'Crisis (70% compression, < 30 days)' },
    { value: 'intensive', label: 'Intensive (50% compression, 30-60 days)' },
    { value: 'steady', label: 'Steady (25% compression, 60-120 days)' },
    { value: 'comprehensive', label: 'Comprehensive (no compression, 120+ days)' }
  ];

  const dependencyTypeOptions = [
    { value: 'sequential', label: 'Sequential (must complete previous task)' },
    { value: 'logical', label: 'Logical (makes sense to complete together)' },
    { value: 'cascade', label: 'Cascade (completion triggers other tasks)' }
  ];

  const estimatedHoursRanges = [
    { value: 'quick', label: 'Quick (< 1 hour)' },
    { value: 'short', label: 'Short (1-2 hours)' },
    { value: 'medium', label: 'Medium (2-4 hours)' },
    { value: 'long', label: 'Long (4+ hours)' }
  ];
  // Simplified anchor system - organized by category
  const anchorOptions = [
    // === CORE APPLICATION ANCHORS (recommended) ===
    { value: 'earliest_application_deadline', label: '🎯 Earliest Application Deadline', description: 'For college-agnostic tasks - uses student\'s earliest deadline across all colleges' },
    { value: 'college_application_deadline', label: '🎯 College Application Deadline (Smart)', description: 'For college-specific tasks - automatically uses correct deadline type (ED/EA/RD) for each college' },
    { value: 'all_applications_completed', label: '🎯 All Applications Completed', description: 'For post-application tasks - when student completes ALL application tasks (smart dependency with fallback)' },
    
    // === STANDARD TIMELINE ANCHORS ===
    { value: 'timeline_baseline', label: 'Timeline Baseline', description: 'Student planning start date' },
    { value: 'senior_year_start', label: 'Senior Year Start', description: 'September 1st of senior year' },
    { value: 'graduation', label: 'Graduation', description: 'High school graduation date' },
    
    // === FINANCIAL AID ANCHORS ===
    { value: 'fafsa_opens', label: 'FAFSA Opens', description: 'October 1st federal FAFSA opening' },
    { value: 'css_profile_opens', label: 'CSS Profile Opens', description: 'CSS Profile availability date' },
    { value: 'finaid_deadline', label: 'Financial Aid Deadline', description: 'College financial aid deadline' },
    
    // === OTHER ANCHORS ===
    { value: 'account_creation', label: 'Account Creation', description: 'When student account was created' },
    { value: 'common_app_opens', label: 'Common App Opens', description: 'August 1st Common Application opening' },
    { value: 'national_decision_day', label: 'National Decision Day', description: 'May 1st college commitment deadline' },
    { value: 'task_completion', label: 'Task Completion', description: 'When a prerequisite task is completed' }
  ];

  const offsetDirections = [
    { value: 'before', label: 'Before' },
    { value: 'after', label: 'After' }
  ];

  const batchGroups = [
    { value: 'core_application', label: 'Core Application' },
    { value: 'writing_intensive', label: 'Writing Intensive' },
    { value: 'test_preparation', label: 'Test Preparation' },
    { value: 'financial_planning', label: 'Financial Planning' },
    { value: 'profile_building', label: 'Profile Building' },
    { value: 'college_research', label: 'College Research' },
    { value: 'general', label: 'General' }
  ];

  // Enhanced preview calculation using interpolation bounds
  const calculatePreviewDueDates = (template) => {
    // Get anchor base date
    const getAnchorDate = (anchorId) => {
      const baseDate = new Date();
      const currentYear = baseDate.getFullYear();
      
      switch (anchorId) {
        case 'timeline_baseline':
          return new Date(currentYear, 8, 1); // September 1st
        case 'senior_year_start':
          return new Date(currentYear + 1, 8, 1); // Next September 1st
        case 'ed_deadline':
        case 'ea_deadline':
          return new Date(currentYear + 1, 10, 15); // November 15th next year
        case 'rd_deadline':
          return new Date(currentYear + 1, 11, 31); // December 31st next year
        case 'fafsa_opens':
          return new Date(currentYear + 1, 9, 1); // October 1st next year
        case 'common_app_opens':
          return new Date(currentYear + 1, 7, 1); // August 1st next year
        case 'national_decision_day':
          return new Date(currentYear + 1, 4, 1); // May 1st next year
        case 'graduation':
          return new Date(currentYear + 1, 5, 15); // June 15th next year
        default:
          return baseDate;
      }
    };

    const anchorDate = getAnchorDate(template.anchor_id);
    const results = {};

    // Calculate RD dates if bounds exist
    if (template.max_rd_offset_days !== null || template.min_rd_offset_days !== null) {
      if (template.max_rd_offset_days !== null) {
        const maxDate = new Date(anchorDate);
        maxDate.setDate(maxDate.getDate() + template.max_rd_offset_days);
        results.maxRD = maxDate;
      }

      if (template.min_rd_offset_days !== null) {
        const minDate = new Date(anchorDate);
        minDate.setDate(minDate.getDate() + template.min_rd_offset_days);
        results.minRD = minDate;
      }
    }

    // Calculate ED dates if bounds exist
    if (template.max_ed_offset_days !== null || template.min_ed_offset_days !== null) {
      if (template.max_ed_offset_days !== null) {
        const maxDate = new Date(anchorDate);
        maxDate.setDate(maxDate.getDate() + template.max_ed_offset_days);
        results.maxED = maxDate;
      }

      if (template.min_ed_offset_days !== null) {
        const minDate = new Date(anchorDate);
        minDate.setDate(minDate.getDate() + template.min_ed_offset_days);
        results.minED = minDate;
      }
    }

    // Legacy single offset fallback
    if (Object.keys(results).length === 0 && template.offset_days) {
      const legacyDate = new Date(anchorDate);
      legacyDate.setDate(legacyDate.getDate() + template.offset_days);
      results.legacy = legacyDate;
    }

    return results;
  };

  // Helper function to calculate preview due date (legacy compatibility)
  const calculatePreviewDueDate = (template) => {
    const dates = calculatePreviewDueDates(template);
    // Return the earliest date for display
    const allDates = Object.values(dates).filter(d => d instanceof Date);
    return allDates.length > 0 ? allDates.sort((a, b) => a - b)[0] : new Date();
  };

  // Helper function to format date and determine if it's overdue
  const formatPreviewDate = (template) => {
    const dueDate = calculatePreviewDueDate(template);
    const today = new Date();
    const isOverdue = dueDate < today;
    const daysDiff = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
    
    const dateStr = dueDate.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: dueDate.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    });
    
    return {
      dateStr,
      isOverdue,
      daysDiff,
      className: isOverdue ? 'text-red-600 font-medium' : 
                 daysDiff < 7 ? 'text-orange-600' : 
                 'text-slate-600'
    };
  };

  useEffect(() => {
    loadTemplates();
    loadAllSubtasks();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('milestone_templates')
        .select('*')
        .order('category', { ascending: true })
        .order('phase', { ascending: true })
        .order('title', { ascending: true });

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingId) {
        // Update existing
        const { error } = await supabase
          .from('milestone_templates')
          .update(formData)
          .eq('id', editingId);

        if (error) throw error;
      } else {
        // Create new
        const { error } = await supabase
          .from('milestone_templates')
          .insert([formData]);

        if (error) throw error;
      }

      await loadTemplates();
      resetForm();
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template');
    }
  };

  // Inline update handler for auto-save
  const handleInlineUpdate = async (templateId, updates) => {
    try {
      const { error } = await supabase
        .from('milestone_templates')
        .update(updates)
        .eq('id', templateId);
      
      if (error) throw error;
      
      // Update local state immediately for responsive UI
      setTemplates(prev => prev.map(t => 
        t.id === templateId ? { ...t, ...updates } : t
      ));
    } catch (error) {
      console.error('Error updating template:', error);
      throw error;
    }
  };

  const handleEdit = (template) => {
    setEditingId(template.id);
    setFormData({
      category: template.category,
      phase: template.phase,
      title: template.title,
      description: template.description || '',
      anchor_id: template.anchor_id || 'timeline_baseline',
      // Single signed offset (maintain compatibility)
      offset_days: template.offset_days || 7,
      // New interpolation bounds system (signed offsets)
      max_rd_offset_days: template.max_rd_offset_days,
      min_rd_offset_days: template.min_rd_offset_days,
      max_ed_offset_days: template.max_ed_offset_days,
      min_ed_offset_days: template.min_ed_offset_days,
      // Target date fields
      max_rd_target_date: template.max_rd_target_date,
      min_rd_target_date: template.min_rd_target_date,
      max_ed_target_date: template.max_ed_target_date,
      min_ed_target_date: template.min_ed_target_date,
      // New 3-field intensity system
      importance_level: template.importance_level || 'medium',
      cognitive_intensity: template.cognitive_intensity || 'medium',
      timing_sensitivity: template.timing_sensitivity || 'medium',
      // Legacy intensity (fallback)
      intensity_level: template.intensity_level || 'standard',
      // Core fields
      priority: template.priority || 'medium',
      is_active: template.is_active !== false,
      is_college_specific: template.is_college_specific || false,
      tier_level: template.tier_level || 'tier1',
      generation_timing: template.generation_timing || 'with_deadlines',
      estimated_duration: template.estimated_duration || 60,
      capacity_weight: template.capacity_weight || 1,
      batch_group: template.batch_group || '',
      // Dependencies
      depends_on_template_id: template.depends_on_template_id || null,
      dependency_type: template.dependency_type || 'sequential',
      dependency_constraints: template.dependency_constraints || {},
      depends_on_template_ids: template.depends_on_template_ids || [],
      prerequisite_tasks: template.prerequisite_tasks || [],
      // Timing modifiers
      timeline_pressure_modifier: template.timeline_pressure_modifier || {},
      application_round_modifier: template.application_round_modifier || {},
      business_days_only: template.business_days_only || false,
      avoid_weekends: template.avoid_weekends !== false,
      minimum_lead_days: template.minimum_lead_days || 7,
      ed_priority_bump_days: template.ed_priority_bump_days || 0,
      // Resources and navigation
      module_name: template.module_name || '',
      module_link: template.module_link || '',
      action_route: template.action_route || '',
      action_label: template.action_label || '',
      resource_urls: template.resource_urls || [],
      // Additional schema fields
      epic_id: template.epic_id || '',
      native_pressure: template.native_pressure || 'comprehensive',
      show_in_min_views: template.show_in_min_views !== false,
      // Workflow organization
      workflow_key: template.workflow_key || '',
      sub_workflow_key: template.sub_workflow_key || '',
      // MVV (Minimum Viable Version) fields
      min_viable_version: template.min_viable_version || '',
      mvv_time_required: template.mvv_time_required || null,
      mvv_risk: template.mvv_risk || null,
      // Optimal version fields
      optimal_version: template.optimal_version || '',
      optimal_time_required: template.optimal_time_required || null,
      incompletion_cost: template.incompletion_cost || null,
      // Completion rule
      completion_rule: template.completion_rule || '',
      // Metadata
      metadata: template.metadata || {}
    });
    setActiveFormTab('basic'); // Reset to first tab
    setShowAddForm(true);
    // Scroll to top of page so user sees the form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('milestone_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Failed to delete template');
    }
  };

  const resetForm = () => {
    setFormData({
      category: '',
      phase: '',
      title: '',
      description: '',
      anchor_id: 'timeline_baseline',
      offset_days: 7,
      depends_on_template_id: null,
      timeline_pressure_modifier: {},
      application_round_modifier: {},
      business_days_only: false,
      avoid_weekends: true,
      minimum_lead_days: 7,
      ed_priority_bump_days: 0,
      batch_group: '',
      capacity_weight: 1,
      priority: 'medium',
      intensity_level: 'standard',
      is_active: true,
      is_college_specific: false,
      tier_level: 'tier1',
      generation_timing: 'with_deadlines',
      estimated_duration: 60,
      module_name: '',
      module_link: '',
      resource_urls: []
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  // Helper function to categorize templates by workload capacity compatibility
  const getWorkloadCapacityLevel = (template) => {
    const hours = template.estimated_duration || 60;
    const hoursDecimal = hours / 60; // Convert minutes to hours
    if (hoursDecimal < 1) return 'light';
    if (hoursDecimal <= 2) return 'moderate';
    return 'intensive';
  };

  // Helper function to categorize templates by disclosure pace compatibility
  const getDisclosurePaceLevel = (template) => {
    if (template.priority === 'critical' || template.priority === 'high') return 'conservative';
    if (template.priority === 'medium' || template.tier_level === 'tier1') return 'balanced';
    return 'aggressive';
  };

  // Helper function to categorize templates by timeline pressure suitability
  const getTimelinePressureLevel = (template) => {
    // Check if template has timeline pressure modifiers configured
    const hasPressureModifiers = template.timeline_pressure_modifier && 
                                 Object.keys(template.timeline_pressure_modifier).length > 0;
    
    // If it has crisis mode configured, it's crisis-compatible
    if (hasPressureModifiers && template.timeline_pressure_modifier.crisis) {
      return 'crisis';
    }
    
    // Critical priority tasks are generally crisis-compatible
    if (template.priority === 'critical') return 'crisis';
    
    // High priority or Tier 1 tasks work for intensive timelines
    if (template.priority === 'high' || template.tier_level === 'tier1') return 'intensive';
    
    // Medium priority tasks work for steady timelines  
    if (template.priority === 'medium' || template.tier_level === 'tier2') return 'steady';
    
    // Everything else works for comprehensive timelines
    return 'comprehensive';
  };

  // Helper function to categorize by estimated hours
  const getEstimatedHoursRange = (template) => {
    const hours = (template.estimated_duration || 60) / 60;
    if (hours < 1) return 'quick';
    if (hours <= 2) return 'short';
    if (hours <= 4) return 'medium';
    return 'long';
  };

  // Filter templates based on current filters
  const filteredTemplates = templates.filter(template => {
    if (filters.category && template.category !== filters.category) return false;
    if (filters.phase && template.phase !== filters.phase) return false;
    if (filters.intensity && template.intensity_level !== filters.intensity) return false;
    if (filters.priority && template.priority !== filters.priority) return false;
    if (filters.generation_timing && template.generation_timing !== filters.generation_timing) return false;
    if (filters.is_active !== 'all') {
      const isActive = filters.is_active === 'true';
      if (template.is_active !== isActive) return false;
    }
    
    // New timeline compatibility filters
    if (filters.workload_capacity && getWorkloadCapacityLevel(template) !== filters.workload_capacity) return false;
    if (filters.disclosure_pace && getDisclosurePaceLevel(template) !== filters.disclosure_pace) return false;
    if (filters.timeline_pressure && getTimelinePressureLevel(template) !== filters.timeline_pressure) return false;
    if (filters.estimated_hours_range && getEstimatedHoursRange(template) !== filters.estimated_hours_range) return false;
    
    return true;
  });

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      category: '',
      phase: '',
      intensity: '',
      priority: '',
      generation_timing: '',
      is_active: 'all',
      workload_capacity: '',
      disclosure_pace: '',
      timeline_pressure: '',
      estimated_hours_range: ''
    });
  };

  const toggleActive = async (template) => {
    try {
      const { error } = await supabase
        .from('milestone_templates')
        .update({ is_active: !template.is_active })
        .eq('id', template.id);

      if (error) throw error;
      await loadTemplates();
    } catch (error) {
      console.error('Error toggling template:', error);
    }
  };

  // Batch update functions
  const toggleTemplateSelection = (templateId) => {
    setSelectedTemplates(prev => {
      if (prev.includes(templateId)) {
        return prev.filter(id => id !== templateId);
      } else {
        return [...prev, templateId];
      }
    });
  };

  const selectAllTemplates = () => {
    const visibleTemplateIds = Object.values(groupedTemplates)
      .flatMap(phases => Object.values(phases))
      .flatMap(templates => templates.map(t => t.id));
    setSelectedTemplates(visibleTemplateIds);
  };

  const clearTemplateSelection = () => {
    setSelectedTemplates([]);
  };

  const handleBatchUpdate = async (e) => {
    e.preventDefault();
    
    if (selectedTemplates.length === 0) return;

    try {
      const updateFields = Object.fromEntries(
        Object.entries(batchUpdateData).filter(([key, value]) => value !== '' && value !== null && value !== undefined)
      );

      if (Object.keys(updateFields).length === 0) {
        alert('Please select at least one field to update');
        return;
      }

      const { error } = await supabase
        .from('milestone_templates')
        .update(updateFields)
        .in('id', selectedTemplates);

      if (error) throw error;

      await loadTemplates();
      setShowBatchModal(false);
      setBatchUpdateData({});
      setSelectedTemplates([]);
      
      alert(`Successfully updated ${selectedTemplates.length} template${selectedTemplates.length !== 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Error batch updating templates:', error);
      alert(`Failed to update templates: ${error.message}`);
    }
  };

  const loadAllSubtasks = async () => {
    try {
      const { data, error } = await supabase
        .from('milestone_template_subtasks')
        .select('*')
        .order('completion_order');
      
      if (error) throw error;
      
      // Group subtasks by parent template ID
      const groupedSubtasks = (data || []).reduce((acc, subtask) => {
        if (!acc[subtask.parent_template_id]) {
          acc[subtask.parent_template_id] = [];
        }
        acc[subtask.parent_template_id].push(subtask);
        return acc;
      }, {});
      
      setSubtasks(groupedSubtasks);
    } catch (error) {
      console.error('Error loading subtasks:', error);
    }
  };

  const loadSubtasksForTemplate = async (templateId) => {
    try {
      const { data, error } = await supabase
        .from('milestone_template_subtasks')
        .select('*')
        .eq('parent_template_id', templateId)
        .order('completion_order');
      
      if (error) throw error;
      
      setSubtasks(prev => ({
        ...prev,
        [templateId]: data || []
      }));
    } catch (error) {
      console.error('Error loading subtasks:', error);
    }
  };

  const saveSubtask = async (templateId) => {
    try {
      // Get the current max completion_order for this template
      const existingSubtasks = subtasks[templateId] || [];
      const maxOrder = existingSubtasks.length > 0 
        ? Math.max(...existingSubtasks.map(s => s.completion_order || 0))
        : -1;
      
      const subtaskData = {
        ...subtaskFormData,
        parent_template_id: templateId,
        // Auto-increment the completion_order
        completion_order: subtaskFormData.completion_order !== 0 
          ? subtaskFormData.completion_order 
          : maxOrder + 1
      };
      
      const { error } = await supabase
        .from('milestone_template_subtasks')
        .insert([subtaskData]);
      
      if (error) throw error;
      
      await loadSubtasksForTemplate(templateId);
      // Reset form but auto-increment the completion_order for next subtask
      setSubtaskFormData({
        title: '',
        description: '',
        offset_from_parent: 0,
        completion_order: maxOrder + 2, // Prepare for next subtask
        is_optional: false
      });
      // Keep the form open for rapid entry
      // setShowSubtaskForm(null);
    } catch (error) {
      console.error('Error saving subtask:', error);
      alert(`Failed to save subtask: ${error.message}`);
    }
  };

  const deleteSubtask = async (subtaskId, templateId) => {
    if (!window.confirm('Delete this subtask?')) return;
    
    try {
      const { error } = await supabase
        .from('milestone_template_subtasks')
        .delete()
        .eq('id', subtaskId);
      
      if (error) throw error;
      
      await loadSubtasksForTemplate(templateId);
    } catch (error) {
      console.error('Error deleting subtask:', error);
      alert('Failed to delete subtask');
    }
  };

  const toggleTemplateExpanded = (templateId) => {
    setExpandedTemplates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(templateId)) {
        newSet.delete(templateId);
      } else {
        newSet.add(templateId);
      }
      return newSet;
    });
  };

  // Group templates by category and sort phases chronologically (using filtered templates)
  // Group templates based on selected grouping option
  const groupedTemplates = filteredTemplates.reduce((acc, template) => {
    if (groupBy === 'workflow') {
      // Group by workflow → sub-workflow
      const workflowKey = template.workflow_key || '_no_workflow';
      const subWorkflowKey = template.sub_workflow_key || '_no_subworkflow';

      if (!acc[workflowKey]) {
        acc[workflowKey] = {};
      }
      if (!acc[workflowKey][subWorkflowKey]) {
        acc[workflowKey][subWorkflowKey] = [];
      }
      acc[workflowKey][subWorkflowKey].push(template);
    } else {
      // Group by category → phase (original)
      if (!acc[template.category]) {
        acc[template.category] = {};
      }
      if (!acc[template.category][template.phase]) {
        acc[template.category][template.phase] = [];
      }
      acc[template.category][template.phase].push(template);
    }
    return acc;
  }, {});

  // Sort sub-groups
  if (groupBy === 'category') {
    Object.keys(groupedTemplates).forEach(category => {
      const sortedPhases = {};
      Object.keys(groupedTemplates[category])
        .sort((a, b) => (phaseOrder[a] || 999) - (phaseOrder[b] || 999))
        .forEach(phase => {
          sortedPhases[phase] = groupedTemplates[category][phase];
        });
      groupedTemplates[category] = sortedPhases;
    });
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold">Milestone Templates</h2>
          <p className="text-sm text-slate-600">
            <span className="text-xs text-stone-500">
              {filteredTemplates.length} of {templates.length} templates
            </span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex items-center bg-stone-100 rounded-lg p-1">
            <button
              onClick={() => setActiveView('table')}
              className={`px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                activeView === 'table'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <List className="h-3 w-3 inline mr-1" />
              Table
            </button>
            <button
              onClick={() => setActiveView('tree')}
              className={`px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                activeView === 'tree'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Target className="h-3 w-3 inline mr-1" />
              Tree
            </button>
          </div>

          {/* Group By Toggle (only for table view) */}
          {activeView === 'table' && (
            <div className="flex items-center bg-stone-100 rounded-lg p-1">
              <button
                onClick={() => setGroupBy('category')}
                className={`px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  groupBy === 'category'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Category
              </button>
              <button
                onClick={() => setGroupBy('workflow')}
                className={`px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  groupBy === 'workflow'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Workflow
              </button>
            </div>
          )}

          <button
            onClick={() => setShowWorkflowEditor(true)}
            className="px-3 py-2 bg-stone-600 text-white rounded-lg hover:bg-stone-700 flex items-center gap-2 text-sm"
          >
            <Edit className="h-4 w-4" />
            Manage Workflows
          </button>

          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 flex items-center gap-2"
          >
            <Plus className="h-5 w-5" />
            {showAddForm ? 'Cancel' : 'Add Template'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow border border-stone-200">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <h3 className="font-medium text-slate-900 flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Filters
            </h3>
            {selectedTemplates.size > 0 && (
              <button
                onClick={() => setShowBatchUpdate(true)}
                className="px-3 py-1 bg-azure-600 text-white text-sm rounded-lg hover:bg-azure-700 flex items-center gap-1"
              >
                <Edit className="h-3 w-3" />
                Batch Update ({selectedTemplates.size})
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowCompatibilityFilters(!showCompatibilityFilters)}
              className="text-sm text-violet-600 hover:text-violet-800 flex items-center gap-1"
            >
              {showCompatibilityFilters ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
              {showCompatibilityFilters ? 'Hide' : 'Show'} Compatibility Filters
            </button>
            <button
              onClick={clearFilters}
              className="text-sm text-azure-600 hover:text-azure-800"
            >
              Clear All
            </button>
            <button
              onClick={() => {
                setShowBatchUpdate(!showBatchUpdate);
                if (!showBatchUpdate) {
                  setSelectedTemplates([]);
                }
              }}
              className={`text-sm flex items-center gap-1 px-2 py-1 rounded ${
                showBatchUpdate
                  ? 'bg-azure-100 text-azure-700 border border-azure-300'
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              {showBatchUpdate ? <Square className="h-3 w-3" /> : <CheckSquare className="h-3 w-3" />}
              {showBatchUpdate ? 'Cancel Batch' : 'Batch Update'}
            </button>
          </div>
        </div>
        
        {/* Primary Filters - Actual Milestone Fields */}
        <div className="mb-4">
          <h4 className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">Milestone Parameters</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Category</label>
            <select
              value={filters.category}
              onChange={(e) => setFilters({...filters, category: e.target.value})}
              className="w-full text-sm border rounded px-2 py-1"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Phase</label>
            <select
              value={filters.phase}
              onChange={(e) => setFilters({...filters, phase: e.target.value})}
              className="w-full text-sm border rounded px-2 py-1"
            >
              <option value="">All Phases</option>
              {phases.map(phase => (
                <option key={phase} value={phase}>{phase}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Intensity Level</label>
            <select
              value={filters.intensity}
              onChange={(e) => setFilters({...filters, intensity: e.target.value})}
              className="w-full text-sm border rounded px-2 py-1"
            >
              <option value="">All Intensities</option>
              {intensityLevels.map(level => (
                <option key={level} value={level}>
                  {level.charAt(0).toUpperCase() + level.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Priority</label>
            <select
              value={filters.priority}
              onChange={(e) => setFilters({...filters, priority: e.target.value})}
              className="w-full text-sm border rounded px-2 py-1"
            >
              <option value="">All Priorities</option>
              {priorities.map(priority => (
                <option key={priority} value={priority}>{priority}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Generation Timing</label>
            <select
              value={filters.generation_timing}
              onChange={(e) => setFilters({...filters, generation_timing: e.target.value})}
              className="w-full text-sm border rounded px-2 py-1"
            >
              <option value="">All Timings</option>
              {generationTimings.map(timing => (
                <option key={timing.value} value={timing.value}>{timing.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Status</label>
            <select
              value={filters.is_active}
              onChange={(e) => setFilters({...filters, is_active: e.target.value})}
              className="w-full text-sm border rounded px-2 py-1"
            >
              <option value="all">All</option>
              <option value="true">Active Only</option>
              <option value="false">Inactive Only</option>
            </select>
          </div>
          </div>
        </div>

        {/* Compatibility Filters - Computed Views (Toggleable) */}
        {showCompatibilityFilters && (
          <div className="border-t pt-4">
            <h4 className="text-xs font-semibold text-violet-700 mb-2 uppercase tracking-wide flex items-center gap-1">
              <Eye className="h-3 w-3" />
              Compatibility Views (Computed from Parameters Above)
            </h4>
            <div className="bg-violet-50 p-3 rounded border border-violet-200">
              <p className="text-xs text-violet-700 mb-3">
                These filters show templates based on calculated compatibility for different student scenarios.
                They are computed from Priority, Estimated Duration, Tier Level, and Time Sensitive settings.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Workload Capacity</label>
            <select
              value={filters.workload_capacity}
              onChange={(e) => setFilters({...filters, workload_capacity: e.target.value})}
              className="w-full text-sm border rounded px-2 py-1"
            >
              <option value="">All Workloads</option>
              {workloadCapacityOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Disclosure Pace</label>
            <select
              value={filters.disclosure_pace}
              onChange={(e) => setFilters({...filters, disclosure_pace: e.target.value})}
              className="w-full text-sm border rounded px-2 py-1"
            >
              <option value="">All Paces</option>
              {disclosurePaceOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Timeline Pressure</label>
            <select
              value={filters.timeline_pressure}
              onChange={(e) => setFilters({...filters, timeline_pressure: e.target.value})}
              className="w-full text-sm border rounded px-2 py-1"
            >
              <option value="">All Pressures</option>
              {timelinePressureOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Estimated Hours</label>
            <select
              value={filters.estimated_hours_range}
              onChange={(e) => setFilters({...filters, estimated_hours_range: e.target.value})}
              className="w-full text-sm border rounded px-2 py-1"
            >
              <option value="">All Durations</option>
              {estimatedHoursRanges.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main content area */}
      <div className="flex flex-col lg:flex-row lg:gap-8">
      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold">
              {editingId ? 'Edit Template' : 'New Template'}
            </h3>
            <button
              type="button"
              onClick={resetForm}
              className="text-stone-500 hover:text-slate-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="flex border-b border-stone-200 mb-6">
            {[
              { key: 'basic', label: 'Basic Info', icon: 'ℹ️' },
              { key: 'workflow', label: 'Workflow & Rules', icon: '🔄' },
              { key: 'mvv', label: 'MVV & Optimal', icon: '⭐' },
              { key: 'timing', label: 'Timing & Offsets', icon: '⏰' },
              { key: 'advanced', label: 'Advanced Settings', icon: '⚙️' }
            ].map(tab => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveFormTab(tab.key)}
                className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${
                  activeFormTab === tab.key
                    ? 'border-azure-500 text-azure-600'
                    : 'border-transparent text-stone-500 hover:text-slate-700 hover:border-stone-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Tab Content */}
            {activeFormTab === 'basic' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                    required
                  >
                    <option value="">Select category...</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Phase *
                  </label>
                  <select
                    value={formData.phase}
                    onChange={(e) => setFormData({...formData, phase: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                    required
                  >
                    <option value="">Select phase...</option>
                    {phases.map(phase => (
                      <option key={phase} value={phase}>{phase}</option>
                    ))}
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                    required
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                    rows="3"
                    placeholder="Detailed description of what the student needs to do"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Priority
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    {priorities.map(priority => (
                      <option key={priority} value={priority}>{priority}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Estimated Duration
                    <span className="text-xs text-stone-500 ml-1">(minutes)</span>
                  </label>
                  <input
                    type="number"
                    value={formData.estimated_duration}
                    onChange={(e) => setFormData({...formData, estimated_duration: parseInt(e.target.value) || 60})}
                    className="w-full border rounded-lg px-3 py-2"
                    min="5"
                    step="5"
                  />
                </div>

                {/* New 3-Field Intensity System */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Importance Level
                  </label>
                  <select
                    value={formData.importance_level}
                    onChange={(e) => setFormData({...formData, importance_level: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    {importanceLevels.map(level => (
                      <option key={level.value} value={level.value}>
                        {level.label} - {level.description}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Cognitive Intensity
                  </label>
                  <select
                    value={formData.cognitive_intensity}
                    onChange={(e) => setFormData({...formData, cognitive_intensity: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    {cognitiveIntensityLevels.map(level => (
                      <option key={level.value} value={level.value}>
                        {level.label} - {level.description}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Timing Sensitivity
                  </label>
                  <select
                    value={formData.timing_sensitivity}
                    onChange={(e) => setFormData({...formData, timing_sensitivity: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    {timingSensitivityLevels.map(level => (
                      <option key={level.value} value={level.value}>
                        {level.label} - {level.description}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Resource URLs
                    <span className="text-xs text-stone-500 ml-1">(one per line)</span>
                  </label>
                  <textarea
                    value={Array.isArray(formData.resource_urls) ? formData.resource_urls.join('\n') : ''}
                    onChange={(e) => {
                      const urls = e.target.value.split('\n').filter(url => url.trim() !== '');
                      setFormData({...formData, resource_urls: urls});
                    }}
                    className="w-full border rounded-lg px-3 py-2"
                    rows="3"
                    placeholder="https://www.commonapp.org/essay-prompts&#10;https://www.collegeessayguy.com/"
                  />
                </div>
              </div>
            )}

            {activeFormTab === 'workflow' && (
              <div className="space-y-6">
                <div className="bg-azure-50 border border-azure-200 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-azure-900 mb-2">🔄 Workflow Organization</h4>
                  <p className="text-sm text-azure-800">
                    Organize this task into a workflow hierarchy. Workflows group related tasks together (e.g., "Secure Recommendations"),
                    and sub-workflows define the sequential steps within that workflow.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Workflow *
                      <span className="text-xs text-stone-500 ml-1">(main category)</span>
                    </label>
                    <select
                      value={formData.workflow_key}
                      onChange={(e) => {
                        setFormData({...formData, workflow_key: e.target.value, sub_workflow_key: ''});
                      }}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      <option value="">No workflow</option>
                      {Object.entries(workflowStructure).map(([key, workflow]) => (
                        <option key={key} value={key}>{workflow.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Sub-Workflow
                      <span className="text-xs text-stone-500 ml-1">(specific step)</span>
                    </label>
                    <select
                      value={formData.sub_workflow_key}
                      onChange={(e) => setFormData({...formData, sub_workflow_key: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                      disabled={!formData.workflow_key}
                    >
                      <option value="">No sub-workflow</option>
                      {formData.workflow_key && workflowStructure[formData.workflow_key]?.subWorkflows &&
                        Object.entries(workflowStructure[formData.workflow_key].subWorkflows).map(([key, label]) => (
                          <option key={key} value={key}>{label}</option>
                        ))
                      }
                    </select>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                  <h4 className="font-semibold text-yellow-900 mb-2">⚡ Completion Rule</h4>
                  <p className="text-sm text-yellow-800 mb-3">
                    Define a rule that auto-completes this task when certain conditions are met.
                  </p>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Completion Rule Name
                      <span className="text-xs text-stone-500 ml-1">(e.g., "finalize_reach_schools")</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={formData.completion_rule}
                        onChange={(e) => setFormData({...formData, completion_rule: e.target.value})}
                        className="flex-1 border rounded-lg px-3 py-2"
                        placeholder="Enter rule name or leave empty"
                      />
                      <button
                        type="button"
                        className="px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 transition-colors"
                        onClick={() => {
                          setCompletionRuleData({
                            rule_name: formData.completion_rule || '',
                            watch_table: 'timeline_items',
                            watch_row_id: '',
                            watch_row_label: '',
                            watch_column: '',
                            operator: '=',
                            value: '',
                            description: ''
                          });
                          setShowCompletionRuleModal(true);
                        }}
                      >
                        Define Trigger
                      </button>
                    </div>
                    <p className="text-xs text-stone-500 mt-1">
                      Click "Define Trigger" to specify when this task should auto-complete
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeFormTab === 'mvv' && (
              <div className="space-y-6">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-emerald-900 mb-2">⭐ Minimum Viable Version (MVV) & Optimal Version</h4>
                  <p className="text-sm text-emerald-800">
                    Define both the minimum acceptable approach (MVV) and the optimal approach for this task.
                    This helps students understand different levels of completion quality.
                  </p>
                </div>

                {/* MVV Section */}
                <div className="border border-stone-200 rounded-lg p-4">
                  <h5 className="font-semibold text-stone-900 mb-3">Minimum Viable Version (MVV)</h5>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        MVV Description
                        <span className="text-xs text-stone-500 ml-1">(quickest acceptable way to complete this)</span>
                      </label>
                      <textarea
                        value={formData.min_viable_version}
                        onChange={(e) => setFormData({...formData, min_viable_version: e.target.value})}
                        className="w-full border rounded-lg px-3 py-2"
                        rows="3"
                        placeholder="e.g., 'Send a simple email thanking each recommender for their time and support.'"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          MVV Time Required
                          <span className="text-xs text-stone-500 ml-1">(minutes)</span>
                        </label>
                        <input
                          type="number"
                          value={formData.mvv_time_required || ''}
                          onChange={(e) => setFormData({...formData, mvv_time_required: e.target.value ? parseInt(e.target.value) : null})}
                          className="w-full border rounded-lg px-3 py-2"
                          min="0"
                          step="5"
                          placeholder="e.g., 15"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          MVV Risk Level
                          <span className="text-xs text-stone-500 ml-1">(0-1, higher = riskier)</span>
                        </label>
                        <input
                          type="number"
                          value={formData.mvv_risk !== null ? formData.mvv_risk : ''}
                          onChange={(e) => setFormData({...formData, mvv_risk: e.target.value ? parseFloat(e.target.value) : null})}
                          className="w-full border rounded-lg px-3 py-2"
                          min="0"
                          max="1"
                          step="0.1"
                          placeholder="e.g., 0.6"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Optimal Section */}
                <div className="border border-stone-200 rounded-lg p-4">
                  <h5 className="font-semibold text-stone-900 mb-3">Optimal Version</h5>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Optimal Description
                        <span className="text-xs text-stone-500 ml-1">(best possible way to complete this)</span>
                      </label>
                      <textarea
                        value={formData.optimal_version}
                        onChange={(e) => setFormData({...formData, optimal_version: e.target.value})}
                        className="w-full border rounded-lg px-3 py-2"
                        rows="3"
                        placeholder="e.g., 'Write personalized handwritten note to each recommender. Mention specific college you were accepted to...'"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Optimal Time Required
                          <span className="text-xs text-stone-500 ml-1">(minutes)</span>
                        </label>
                        <input
                          type="number"
                          value={formData.optimal_time_required || ''}
                          onChange={(e) => setFormData({...formData, optimal_time_required: e.target.value ? parseInt(e.target.value) : null})}
                          className="w-full border rounded-lg px-3 py-2"
                          min="0"
                          step="5"
                          placeholder="e.g., 45"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Incompletion Cost
                          <span className="text-xs text-stone-500 ml-1">(0-1, higher = worse to skip)</span>
                        </label>
                        <input
                          type="number"
                          value={formData.incompletion_cost !== null ? formData.incompletion_cost : ''}
                          onChange={(e) => setFormData({...formData, incompletion_cost: e.target.value ? parseFloat(e.target.value) : null})}
                          className="w-full border rounded-lg px-3 py-2"
                          min="0"
                          max="1"
                          step="0.1"
                          placeholder="e.g., 0.7"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeFormTab === 'timing' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Anchor Point *
                      <span className="text-xs text-stone-500 ml-1">(what date to calculate from)</span>
                    </label>
                    <select
                      value={formData.anchor_id}
                      onChange={(e) => setFormData({...formData, anchor_id: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                      required
                    >
                      {anchorOptions.map(anchor => (
                        <option key={anchor.value} value={anchor.value}>
                          {anchor.label} - {anchor.description}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Migration Warning for Deprecated Anchors */}
                  {formData.anchor_id && ['ed_deadline', 'ea_deadline', 'rd_deadline'].includes(formData.anchor_id) && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h4 className="font-semibold text-red-900 mb-2">🚨 Deprecated Anchor Detected</h4>
                      <p className="text-sm text-red-800 mb-3">
                        This template is using a deprecated anchor: <code className="bg-red-100 px-1 rounded">{formData.anchor_id}</code>
                      </p>
                      <div className="bg-white border border-red-200 rounded p-3 mb-3">
                        <p className="text-sm font-medium text-red-900 mb-2">Recommended Migration:</p>
                        <ul className="text-xs text-red-800 space-y-1">
                          <li>• For <strong>college-specific tasks</strong>: Use <code className="bg-red-100 px-1 rounded">College Application Deadline (Smart)</code></li>
                          <li>• For <strong>college-agnostic tasks</strong>: Use <code className="bg-red-100 px-1 rounded">Earliest Application Deadline</code></li>
                          <li>• The new anchors automatically handle all deadline types (ED/EA/RD)</li>
                        </ul>
                      </div>
                      <p className="text-xs text-red-700">
                        💡 <strong>Tip:</strong> The new system eliminates guesswork and provides clearer intent-driven anchoring.
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Signed Offset Days
                      <span className="text-xs text-stone-500 ml-1">(positive = after anchor, negative = before)</span>
                    </label>
                    <input
                      type="number"
                      value={formData.offset_days}
                      onChange={(e) => setFormData({...formData, offset_days: parseInt(e.target.value) || 0})}
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="7 (positive = after, negative = before)"
                    />
                  </div>
                </div>

                {/* Interpolation Bounds System */}
                <div className="border rounded-lg p-4 bg-azure-50">
                  <h4 className="font-semibold text-azure-900 mb-3">📊 Interpolation Bounds System</h4>
                  <p className="text-sm text-azure-800 mb-4">
                    Set different timing ranges for Regular Decision (RD) and Early Decision (ED) applications using signed offsets.
                    Negative values = before anchor date, Positive values = after anchor date.
                    The system will interpolate between min/max bounds based on timeline pressure.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <h5 className="font-medium text-azure-900">Regular Decision (RD) Bounds</h5>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-azure-700 mb-1">
                            Max RD Offset (signed)
                            <span className="text-xs text-stone-500 block">negative = before, positive = after</span>
                          </label>
                          <input
                            type="number"
                            value={formData.max_rd_offset_days || ''}
                            onChange={(e) => setFormData({...formData, max_rd_offset_days: e.target.value ? parseInt(e.target.value) : null})}
                            className="w-full border rounded px-2 py-1 text-sm"
                            placeholder="-60 (60 days before)"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-azure-700 mb-1">
                            Min RD Offset (signed)
                            <span className="text-xs text-stone-500 block">negative = before, positive = after</span>
                          </label>
                          <input
                            type="number"
                            value={formData.min_rd_offset_days || ''}
                            onChange={(e) => setFormData({...formData, min_rd_offset_days: e.target.value ? parseInt(e.target.value) : null})}
                            className="w-full border rounded px-2 py-1 text-sm"
                            placeholder="-14 (14 days before)"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h5 className="font-medium text-azure-900">Early Decision (ED) Bounds</h5>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-azure-700 mb-1">
                            Max ED Offset (signed)
                            <span className="text-xs text-stone-500 block">negative = before, positive = after</span>
                          </label>
                          <input
                            type="number"
                            value={formData.max_ed_offset_days || ''}
                            onChange={(e) => setFormData({...formData, max_ed_offset_days: e.target.value ? parseInt(e.target.value) : null})}
                            className="w-full border rounded px-2 py-1 text-sm"
                            placeholder="-90 (90 days before)"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-azure-700 mb-1">
                            Min ED Offset (signed)
                            <span className="text-xs text-stone-500 block">negative = before, positive = after</span>
                          </label>
                          <input
                            type="number"
                            value={formData.min_ed_offset_days || ''}
                            onChange={(e) => setFormData({...formData, min_ed_offset_days: e.target.value ? parseInt(e.target.value) : null})}
                            className="w-full border rounded px-2 py-1 text-sm"
                            placeholder="-21 (21 days before)"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Legacy Single Offset (for compatibility) */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-medium text-yellow-800 mb-2">📅 Single Signed Offset</h4>
                  <p className="text-sm text-yellow-700 mb-3">
                    For templates without interpolation bounds, this single signed offset will be used.
                    Positive = after anchor, Negative = before anchor.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-yellow-700 mb-1">
                        Signed Offset Days
                        <span className="text-xs text-slate-600 block">e.g., -30 = 30 days before anchor</span>
                      </label>
                      <input
                        type="number"
                        value={formData.offset_days}
                        onChange={(e) => setFormData({...formData, offset_days: parseInt(e.target.value) || 0})}
                        className="w-full border rounded-lg px-3 py-2"
                        placeholder="-30 (30 days before)"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-yellow-700 mb-1">
                        Minimum Lead Days
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={formData.minimum_lead_days}
                        onChange={(e) => setFormData({...formData, minimum_lead_days: parseInt(e.target.value) || 7})}
                        className="w-full border rounded-lg px-3 py-2"
                      />
                    </div>
                  </div>
                </div>

                {/* Fallback Anchor System - For Smart Dependencies */}
                {formData.anchor_id === 'all_applications_completed' && (
                  <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
                    <h4 className="font-semibold text-violet-900 mb-3">🔄 Smart Fallback System</h4>
                    <p className="text-sm text-violet-800 mb-4">
                      Configure a fallback anchor for when the primary condition isn't met. 
                      Example: If all applications aren't completed, show task on Jan 2 anyway.
                    </p>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-violet-700 mb-1">
                          Fallback Anchor
                          <span className="text-xs text-stone-500 block">Used when primary anchor fails</span>
                        </label>
                        <select
                          value={formData.fallback_anchor_id || ''}
                          onChange={(e) => setFormData({...formData, fallback_anchor_id: e.target.value || null})}
                          className="w-full border rounded-lg px-3 py-2 text-sm"
                        >
                          <option value="">No fallback (optional)</option>
                          {anchorOptions.filter(a => a.value !== formData.anchor_id).map(anchor => (
                            <option key={anchor.value} value={anchor.value}>
                              {anchor.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-violet-700 mb-1">
                          Fallback Offset Days
                          <span className="text-xs text-stone-500 block">Applied to fallback anchor</span>
                        </label>
                        <input
                          type="number"
                          value={formData.fallback_offset_days || ''}
                          onChange={(e) => setFormData({...formData, fallback_offset_days: e.target.value ? parseInt(e.target.value) : null})}
                          className="w-full border rounded-lg px-3 py-2 text-sm"
                          placeholder="1 (day after fallback anchor)"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* College Visibility Settings - For College-Specific Templates */}
                {formData.is_college_specific && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <h4 className="font-semibold text-orange-900 mb-3">🏫 College Visibility Settings</h4>
                    <p className="text-sm text-orange-800 mb-4">
                      Control which college types should show this task. Leave all unchecked to show for all college types.
                    </p>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={formData.show_for_ed_schools !== false}
                            onChange={(e) => setFormData({...formData, show_for_ed_schools: e.target.checked})}
                            className="rounded"
                          />
                          <span className="text-sm font-medium text-orange-700">Early Decision Schools</span>
                        </label>
                        
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={formData.show_for_ea_schools !== false}
                            onChange={(e) => setFormData({...formData, show_for_ea_schools: e.target.checked})}
                            className="rounded"
                          />
                          <span className="text-sm font-medium text-orange-700">Early Action Schools</span>
                        </label>
                      </div>
                      
                      <div className="space-y-2">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={formData.show_for_rd_schools !== false}
                            onChange={(e) => setFormData({...formData, show_for_rd_schools: e.target.checked})}
                            className="rounded"
                          />
                          <span className="text-sm font-medium text-orange-700">Regular Decision Schools</span>
                        </label>
                        
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={formData.show_for_waitlisted_schools === true}
                            onChange={(e) => setFormData({...formData, show_for_waitlisted_schools: e.target.checked})}
                            className="rounded"
                          />
                          <span className="text-sm font-medium text-orange-700">Waitlisted Schools</span>
                        </label>
                        
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={formData.show_for_accepted_schools !== false}
                            onChange={(e) => setFormData({...formData, show_for_accepted_schools: e.target.checked})}
                            className="rounded"
                          />
                          <span className="text-sm font-medium text-orange-700">Accepted Schools</span>
                        </label>
                        
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={formData.show_for_selected_school === true}
                            onChange={(e) => setFormData({...formData, show_for_selected_school: e.target.checked})}
                            className="rounded"
                          />
                          <span className="text-sm font-medium text-orange-700">Selected School</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* Date Calculation Preview */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <h4 className="font-medium text-emerald-800 mb-2">📅 Due Date Preview</h4>
                  <div className="text-sm text-emerald-700">
                    {formData.anchor_id && (
                      <div className="space-y-1">
                        <div><strong>Anchor:</strong> {anchorOptions.find(a => a.value === formData.anchor_id)?.label}</div>
                        <div><strong>RD Due Date:</strong> {calculatePreviewDueDates(formData).minRD?.toLocaleDateString() || 'Not calculated'}</div>
                        <div><strong>ED Due Date:</strong> {calculatePreviewDueDates(formData).minED?.toLocaleDateString() || 'Not calculated'}</div>
                        <div><strong>Legacy Due Date:</strong> {calculatePreviewDueDates(formData).legacy?.toLocaleDateString() || 'Not calculated'}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeFormTab === 'advanced' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Tier Level
                    </label>
                    <select
                      value={formData.tier_level}
                      onChange={(e) => setFormData({...formData, tier_level: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      {tierLevels.map(tier => (
                        <option key={tier.value} value={tier.value}>{tier.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Generation Timing
                    </label>
                    <select
                      value={formData.generation_timing}
                      onChange={(e) => setFormData({...formData, generation_timing: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      {generationTimings.map(timing => (
                        <option key={timing.value} value={timing.value}>{timing.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Batch Group
                      <span className="text-xs text-stone-500 ml-1">(workload management)</span>
                    </label>
                    <select
                      value={formData.batch_group}
                      onChange={(e) => setFormData({...formData, batch_group: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      <option value="">No batch group</option>
                      {batchGroups.map(group => (
                        <option key={group.value} value={group.value}>{group.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Capacity Weight
                      <span className="text-xs text-stone-500 ml-1">(1-4, higher = more intensive)</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="4"
                      value={formData.capacity_weight}
                      onChange={(e) => setFormData({...formData, capacity_weight: parseInt(e.target.value) || 1})}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Prerequisites
                      <span className="text-xs text-stone-500 ml-1">(tasks that should complete first)</span>
                    </label>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {formData.prerequisite_tasks?.map((prereq, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-stone-50 rounded">
                          <select
                            value={prereq.template_id || ''}
                            onChange={(e) => {
                              const newPrereqs = [...formData.prerequisite_tasks];
                              newPrereqs[index].template_id = e.target.value;
                              setFormData({...formData, prerequisite_tasks: newPrereqs});
                            }}
                            className="flex-1 border rounded px-2 py-1 text-sm"
                          >
                            <option value="">Select prerequisite...</option>
                            {templates
                              .filter(t => t.id !== editingId && t.id !== formData.id &&
                                      !formData.prerequisite_tasks.some((p, i) => i !== index && p.template_id === t.id))
                              .map(template => (
                              <option key={template.id} value={template.id}>
                                {template.category}: {template.title.substring(0, 50)}{template.title.length > 50 ? '...' : ''}
                              </option>
                            ))}
                          </select>
                          <select
                            value={prereq.type || 'recommended'}
                            onChange={(e) => {
                              const newPrereqs = [...formData.prerequisite_tasks];
                              newPrereqs[index].type = e.target.value;
                              setFormData({...formData, prerequisite_tasks: newPrereqs});
                            }}
                            className="border rounded px-2 py-1 text-sm"
                          >
                            <option value="required">Required</option>
                            <option value="recommended">Recommended</option>
                          </select>
                          <button
                            type="button"
                            onClick={() => {
                              const newPrereqs = formData.prerequisite_tasks.filter((_, i) => i !== index);
                              setFormData({...formData, prerequisite_tasks: newPrereqs});
                            }}
                            className="p-1 text-red-500 hover:text-red-700"
                          >
                            <Trash className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      {(!formData.prerequisite_tasks || formData.prerequisite_tasks.length === 0) && (
                        <div className="text-sm text-stone-500 italic p-2">
                          No prerequisites defined
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            prerequisite_tasks: [...(formData.prerequisite_tasks || []), {template_id: '', type: 'recommended'}]
                          });
                        }}
                        className="flex items-center gap-2 px-3 py-1 text-sm text-azure-600 hover:text-azure-800 border border-azure-300 rounded hover:bg-azure-50"
                      >
                        <Plus className="h-4 w-4" />
                        Add Prerequisite
                      </button>
                    </div>
                  </div>
                </div>

                {/* Module Integration */}
                <div className="border rounded-lg p-4 bg-stone-50">
                  <h4 className="font-semibold text-slate-900 mb-3">🔗 Module Integration</h4>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Module Name
                      </label>
                      <input
                        type="text"
                        value={formData.module_name}
                        onChange={(e) => setFormData({...formData, module_name: e.target.value})}
                        placeholder="Essay Builder"
                        className="w-full border rounded-lg px-3 py-2"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Module Link / URL Pattern
                      </label>

                      {/* Quick Pattern Dropdown */}
                      <div className="mb-2">
                        <select
                          value=""
                          onChange={(e) => {
                            if (e.target.value) {
                              setFormData({...formData, module_link: e.target.value});
                            }
                          }}
                          className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                        >
                          <option value="">Select common pattern or enter custom...</option>
                          <option value="/college/enhanced-strategy/{{college_id}}">College Strategy Page</option>
                          <option value="/college/essays/{{college_id}}">College Essays Page</option>
                          <option value="/college/requirements/{{college_id}}">College Requirements</option>
                          <option value="/student/timeline">Student Timeline</option>
                          <option value="/student/profile">Student Profile</option>
                          <option value="/resources/test-prep">Test Prep Resources</option>
                          <option value="/resources/financial-aid">Financial Aid Resources</option>
                        </select>
                      </div>

                      {/* Manual Input */}
                      <input
                        type="text"
                        value={formData.module_link}
                        onChange={(e) => setFormData({...formData, module_link: e.target.value})}
                        placeholder="/college/essays or /college/strategy/{{college_id}}"
                        className="w-full border rounded-lg px-3 py-2"
                      />

                      {/* Pattern Help */}
                      <div className="mt-2 text-xs text-slate-600">
                        <div className="flex flex-wrap gap-2">
                          <span className="px-2 py-1 bg-azure-100 rounded">{'{{college_id}}'} → College ID</span>
                          <span className="px-2 py-1 bg-emerald-100 rounded">{'{{college_name}}'} → College Name</span>
                        </div>
                        <p className="mt-1">Use patterns for college-specific tasks to automatically generate correct URLs.</p>
                      </div>

                      {/* Preview for College-Specific Tasks */}
                      {formData.is_college_specific && formData.module_link && formData.module_link.includes('{{') && (
                        <div className="mt-3 p-3 bg-azure-50 border border-azure-200 rounded-lg">
                          <h5 className="text-sm font-medium text-azure-900 mb-2">Preview URLs:</h5>
                          <div className="space-y-1 text-sm">
                            <div className="flex items-center gap-2">
                              <span className="text-slate-600">Harvard:</span>
                              <code className="text-azure-700 bg-white px-2 py-1 rounded text-xs">
                                {(formData.module_link || '').replace(/\{\{college_id\}\}/g, '123').replace(/\{\{college_name\}\}/g, 'Harvard')}
                              </code>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-slate-600">Stanford:</span>
                              <code className="text-azure-700 bg-white px-2 py-1 rounded text-xs">
                                {(formData.module_link || '').replace(/\{\{college_id\}\}/g, '456').replace(/\{\{college_name\}\}/g, 'Stanford')}
                              </code>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>



                {/* Settings Checkboxes */}
                <div className="border rounded-lg p-4 bg-stone-50">
                  <h4 className="font-semibold text-slate-900 mb-3">⚙️ Settings</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_active"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                        className="mr-2"
                      />
                      <label htmlFor="is_active" className="text-sm font-medium text-slate-700">
                        Active
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_college_specific"
                        checked={formData.is_college_specific}
                        onChange={(e) => setFormData({...formData, is_college_specific: e.target.checked})}
                        className="mr-2"
                      />
                      <label htmlFor="is_college_specific" className="text-sm font-medium text-slate-700">
                        College Specific
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="business_days_only"
                        checked={formData.business_days_only}
                        onChange={(e) => setFormData({...formData, business_days_only: e.target.checked})}
                        className="mr-2"
                      />
                      <label htmlFor="business_days_only" className="text-sm font-medium text-slate-700">
                        Business Days Only
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="avoid_weekends"
                        checked={formData.avoid_weekends}
                        onChange={(e) => setFormData({...formData, avoid_weekends: e.target.checked})}
                        className="mr-2"
                      />
                      <label htmlFor="avoid_weekends" className="text-sm font-medium text-slate-700">
                        Avoid Weekends
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Form Actions */}
            <div className="flex justify-between pt-4 border-t border-stone-200">
              <button
                type="button"
                onClick={() => {
                  if (activeFormTab === 'basic') {
                    resetForm();
                  } else {
                    // Navigate to previous tab
                    const tabOrder = ['basic', 'timing', 'advanced'];
                    const currentIndex = tabOrder.indexOf(activeFormTab);
                    setActiveFormTab(tabOrder[currentIndex - 1]);
                  }
                }}
                className="px-4 py-2 border border-stone-300 rounded-lg hover:bg-stone-50"
              >
                {activeFormTab === 'basic' ? 'Cancel' : 'Previous'}
              </button>

              <div className="flex gap-2">
                {activeFormTab !== 'advanced' && (
                  <button
                    type="button"
                    onClick={() => {
                      // Navigate to next tab
                      const tabOrder = ['basic', 'timing', 'advanced'];
                      const currentIndex = tabOrder.indexOf(activeFormTab);
                      setActiveFormTab(tabOrder[currentIndex + 1]);
                    }}
                    className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700"
                  >
                    Next
                  </button>
                )}
                
                <button
                  type="submit"
                  className="px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700"
                >
                  {editingId ? 'Update' : 'Create'} Template
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
      </div>

      {/* Additional content area */}
      <div className="flex flex-col lg:flex-row lg:gap-8">
      {/* Batch Update Controls */}
      {showBatchUpdate && (
        <div className="bg-azure-50 border border-azure-200 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-azure-700">
                Batch Update Mode: {selectedTemplates.length} template{selectedTemplates.length !== 1 ? 's' : ''} selected
              </span>
              <div className="flex gap-2">
                <button
                  onClick={selectAllTemplates}
                  className="text-xs px-2 py-1 bg-azure-100 text-azure-700 rounded hover:bg-azure-200"
                >
                  Select All
                </button>
                <button
                  onClick={() => setSelectedTemplates([])}
                  className="text-xs px-2 py-1 bg-stone-100 text-slate-700 rounded hover:bg-stone-200"
                >
                  Clear Selection
                </button>
              </div>
            </div>
            {selectedTemplates.length > 0 && (
              <button
                onClick={() => setShowBatchModal(true)}
                className="px-3 py-1 bg-azure-600 text-white rounded text-sm hover:bg-azure-700"
              >
                Update Selected
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Streamlined Templates Table or Tree View */}
      <div className="bg-white rounded-lg shadow-sm border">
        {loading ? (
          <div className="text-center py-8 text-stone-500">Loading templates...</div>
        ) : Object.keys(groupedTemplates).length === 0 ? (
          <div className="text-center py-8 text-stone-500">No templates found</div>
        ) : activeView === 'table' ? (
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-stone-200">
              <thead className="bg-stone-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                    Template
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                    Timing
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                    Details
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                    Crisis Support
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-stone-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-stone-200">
                {Object.entries(groupedTemplates).flatMap(([groupKey, subGroups]) => {
                  // Get group display name
                  const groupDisplayName = groupBy === 'workflow'
                    ? (groupKey === '_no_workflow'
                        ? '🔴 No Workflow Assigned'
                        : workflowStructure[groupKey]?.label || groupKey)
                    : groupKey;

                  return Object.entries(subGroups).flatMap(([subGroupKey, templates], subIndex) => {
                    // Get sub-group display name
                    const subGroupDisplayName = groupBy === 'workflow'
                      ? (subGroupKey === '_no_subworkflow'
                          ? 'Unassigned'
                          : workflowStructure[groupKey]?.subWorkflows[subGroupKey] || subGroupKey)
                      : subGroupKey;

                    // Create section header row
                    const headerRow = (
                      <tr key={`header-${groupKey}-${subGroupKey}`} className="bg-stone-50">
                        <td colSpan="6" className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${
                              groupBy === 'workflow'
                                ? (groupKey === '_no_workflow' ? 'bg-red-500' : 'bg-azure-500')
                                : 'bg-slate-500'
                            }`} />
                            <span className="font-semibold text-sm text-slate-900">
                              {groupDisplayName}
                            </span>
                            <span className="text-slate-400">/</span>
                            <span className="font-medium text-sm text-slate-700">
                              {subGroupDisplayName}
                            </span>
                            <span className="text-xs text-stone-500 ml-2">
                              ({templates.length} {templates.length === 1 ? 'task' : 'tasks'})
                            </span>
                          </div>
                        </td>
                      </tr>
                    );

                    // Create template rows
                    const templateRows = templates.map(template => (
                      <TemplateRow
                        key={template.id}
                        template={template}
                        category={groupKey}
                        phase={subGroupKey}
                        onUpdate={handleInlineUpdate}
                        onDelete={handleDelete}
                        onEdit={handleEdit}
                        templates={templates}
                      />
                    ));

                    return [headerRow, ...templateRows];
                  });
                })}
              </tbody>
            </table>
          </div>
        ) : (
          /* Tree View */
          <div className="p-6">
            {Object.entries(workflowStructure).map(([workflowKey, workflow]) => {
              const workflowTemplates = filteredTemplates.filter(t => t.workflow_key === workflowKey);
              const hasTemplates = workflowTemplates.length > 0;

              if (!hasTemplates) return null;

              return (
                <div key={workflowKey} className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <button
                      onClick={() => {
                        const expanded = new Set(expandedTemplates);
                        if (expanded.has(workflowKey)) {
                          expanded.delete(workflowKey);
                        } else {
                          expanded.add(workflowKey);
                        }
                        setExpandedTemplates(expanded);
                      }}
                      className="p-1 hover:bg-stone-100 rounded"
                    >
                      {expandedTemplates.has(workflowKey) ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-azure-500" />
                      <h3 className="font-semibold text-lg text-slate-900">{workflow.label}</h3>
                      <span className="text-sm text-stone-500">({workflowTemplates.length} tasks)</span>
                    </div>
                  </div>

                  {expandedTemplates.has(workflowKey) && (
                    <div className="ml-8 space-y-4">
                      {Object.entries(workflow.subWorkflows).map(([subKey, subLabel]) => {
                        const subTemplates = workflowTemplates.filter(t => t.sub_workflow_key === subKey);

                        if (subTemplates.length === 0) return null;

                        return (
                          <div key={subKey} className="border-l-2 border-stone-200 pl-4">
                            <div className="flex items-center gap-2 mb-2">
                              <button
                                onClick={() => {
                                  const expanded = new Set(expandedTemplates);
                                  const subKey2 = `${workflowKey}-${subKey}`;
                                  if (expanded.has(subKey2)) {
                                    expanded.delete(subKey2);
                                  } else {
                                    expanded.add(subKey2);
                                  }
                                  setExpandedTemplates(expanded);
                                }}
                                className="p-1 hover:bg-stone-100 rounded"
                              >
                                {expandedTemplates.has(`${workflowKey}-${subKey}`) ? (
                                  <ChevronDown className="h-3 w-3" />
                                ) : (
                                  <ChevronRight className="h-3 w-3" />
                                )}
                              </button>
                              <div className="w-2 h-2 rounded-full bg-emerald-500" />
                              <h4 className="font-medium text-slate-700">{subLabel}</h4>
                              <span className="text-xs text-stone-500">({subTemplates.length})</span>
                            </div>

                            {expandedTemplates.has(`${workflowKey}-${subKey}`) && (
                              <div className="ml-6 space-y-2">
                                {subTemplates.map(template => (
                                  <div
                                    key={template.id}
                                    className="group flex items-center justify-between p-3 bg-stone-50 hover:bg-stone-100 rounded-lg cursor-pointer transition-colors"
                                    onClick={() => handleEdit(template)}
                                  >
                                    <div className="flex items-center gap-3 flex-1">
                                      <div className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                                      <div className="flex-1">
                                        <div className="font-medium text-slate-900 group-hover:text-azure-600">
                                          {template.title}
                                        </div>
                                        {template.description && (
                                          <div className="text-xs text-stone-500 mt-0.5 line-clamp-1">
                                            {template.description}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {template.completion_rule && (
                                        <div className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded flex items-center gap-1">
                                          <span>⚡</span>
                                          <span>Rule</span>
                                        </div>
                                      )}
                                      {template.min_viable_version && (
                                        <div className="px-2 py-1 bg-emerald-100 text-emerald-800 text-xs rounded flex items-center gap-1">
                                          <span>⭐</span>
                                          <span>MVV</span>
                                        </div>
                                      )}
                                      <div className="px-2 py-1 bg-stone-200 text-stone-600 text-xs rounded">
                                        {template.category}
                                      </div>
                                      <div className={`px-2 py-1 text-xs rounded ${
                                        template.is_active
                                          ? 'bg-green-100 text-green-800'
                                          : 'bg-stone-300 text-stone-600'
                                      }`}>
                                        {template.is_active ? 'Active' : 'Inactive'}
                                      </div>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEdit(template);
                                        }}
                                        className="p-1 text-azure-600 hover:bg-azure-100 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                      >
                                        <Edit className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {/* Tasks without sub-workflow */}
                      {(() => {
                        const orphanTemplates = workflowTemplates.filter(t => !t.sub_workflow_key);
                        if (orphanTemplates.length === 0) return null;

                        return (
                          <div className="border-l-2 border-stone-200 pl-4">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-2 h-2 rounded-full bg-orange-500" />
                              <h4 className="font-medium text-stone-600 italic">Unassigned</h4>
                              <span className="text-xs text-stone-500">({orphanTemplates.length})</span>
                            </div>
                            <div className="ml-6 space-y-2">
                              {orphanTemplates.map(template => (
                                <div
                                  key={template.id}
                                  className="group flex items-center justify-between p-3 bg-orange-50 hover:bg-orange-100 rounded-lg cursor-pointer transition-colors"
                                  onClick={() => handleEdit(template)}
                                >
                                  <div className="flex items-center gap-3 flex-1">
                                    <div className="w-1.5 h-1.5 rounded-full bg-orange-400" />
                                    <div className="flex-1">
                                      <div className="font-medium text-slate-900 group-hover:text-azure-600">
                                        {template.title}
                                      </div>
                                    </div>
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEdit(template);
                                    }}
                                    className="p-1 text-azure-600 hover:bg-azure-100 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Tasks without workflow */}
            {(() => {
              const orphanTemplates = filteredTemplates.filter(t => !t.workflow_key);
              if (orphanTemplates.length === 0) return null;

              return (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <button
                      onClick={() => {
                        const expanded = new Set(expandedTemplates);
                        if (expanded.has('no-workflow')) {
                          expanded.delete('no-workflow');
                        } else {
                          expanded.add('no-workflow');
                        }
                        setExpandedTemplates(expanded);
                      }}
                      className="p-1 hover:bg-stone-100 rounded"
                    >
                      {expandedTemplates.has('no-workflow') ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      <h3 className="font-semibold text-lg text-red-700">No Workflow Assigned</h3>
                      <span className="text-sm text-stone-500">({orphanTemplates.length} tasks)</span>
                    </div>
                  </div>

                  {expandedTemplates.has('no-workflow') && (
                    <div className="ml-8 space-y-2">
                      {orphanTemplates.map(template => (
                        <div
                          key={template.id}
                          className="group flex items-center justify-between p-3 bg-red-50 hover:bg-red-100 rounded-lg cursor-pointer transition-colors"
                          onClick={() => handleEdit(template)}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
                            <div className="flex-1">
                              <div className="font-medium text-slate-900 group-hover:text-azure-600">
                                {template.title}
                              </div>
                              <div className="text-xs text-stone-500 mt-0.5">
                                {template.category} / {template.phase}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(template);
                            }}
                            className="p-1 text-azure-600 hover:bg-azure-100 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>
      </div>

      {/* Batch Update Modal */}
      {showBatchModal && selectedTemplates.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                  Batch Update {selectedTemplates.length} Milestone{selectedTemplates.length !== 1 ? 's' : ''}
                </h3>
                <button
                  onClick={() => {
                    setShowBatchModal(false);
                    setBatchUpdateData({});
                  }}
                  className="text-stone-400 hover:text-slate-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleBatchUpdate} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Tier Level
                    </label>
                    <select
                      value={batchUpdateData.tier_level || ''}
                      onChange={(e) => setBatchUpdateData(prev => ({
                        ...prev,
                        tier_level: e.target.value || null
                      }))}
                      className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-1 focus:ring-azure-500"
                    >
                      <option value="">Keep current values</option>
                      <option value="global">Global</option>
                      <option value="tier1">T1 Universal</option>
                      <option value="tier2">T2 Standard</option>
                      <option value="tier3">T3 Conditional</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Generation Timing
                    </label>
                    <select
                      value={batchUpdateData.generation_timing || ''}
                      onChange={(e) => setBatchUpdateData(prev => ({
                        ...prev,
                        generation_timing: e.target.value || null
                      }))}
                      className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-1 focus:ring-azure-500"
                    >
                      <option value="">Keep current values</option>
                      <option value="with_global">With Global</option>
                      <option value="immediate">Immediate</option>
                      <option value="with_deadlines">With Deadlines</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Intensity Level
                    </label>
                    <select
                      value={batchUpdateData.intensity_level || ''}
                      onChange={(e) => setBatchUpdateData(prev => ({
                        ...prev,
                        intensity_level: e.target.value || null
                      }))}
                      className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-1 focus:ring-azure-500"
                    >
                      <option value="">Keep current values</option>
                      <option value="crisis">Crisis</option>
                      <option value="standard">Standard</option>
                      <option value="comprehensive">Comprehensive</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Priority
                    </label>
                    <select
                      value={batchUpdateData.priority || ''}
                      onChange={(e) => setBatchUpdateData(prev => ({
                        ...prev,
                        priority: e.target.value || null
                      }))}
                      className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-1 focus:ring-azure-500"
                    >
                      <option value="">Keep current values</option>
                      {priorities.map(priority => (
                        <option key={priority} value={priority}>
                          {priority.charAt(0).toUpperCase() + priority.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Estimated Duration (hours)
                    </label>
                    <input
                      type="number"
                      min="0.5"
                      step="0.5"
                      placeholder="Keep current values"
                      value={batchUpdateData.estimated_duration || ''}
                      onChange={(e) => setBatchUpdateData(prev => ({
                        ...prev,
                        estimated_duration: e.target.value ? parseFloat(e.target.value) : null
                      }))}
                      className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-1 focus:ring-azure-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Days Offset
                    </label>
                    <input
                      type="number"
                      placeholder="Keep current values"
                      value={batchUpdateData.days_offset || ''}
                      onChange={(e) => setBatchUpdateData(prev => ({
                        ...prev,
                        days_offset: e.target.value ? parseInt(e.target.value) : null
                      }))}
                      className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-1 focus:ring-azure-500"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="batchIsCollegeSpecific"
                      checked={batchUpdateData.is_college_specific || false}
                      onChange={(e) => setBatchUpdateData(prev => ({
                        ...prev,
                        is_college_specific: e.target.checked
                      }))}
                      className="h-4 w-4 text-azure-600 focus:ring-azure-500 border-stone-300 rounded mr-2"
                    />
                    <label htmlFor="batchIsCollegeSpecific" className="text-sm text-slate-700">
                      Set as College-Specific
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="batchIsActive"
                      checked={batchUpdateData.is_active !== false}
                      onChange={(e) => setBatchUpdateData(prev => ({
                        ...prev,
                        is_active: e.target.checked
                      }))}
                      className="h-4 w-4 text-azure-600 focus:ring-azure-500 border-stone-300 rounded mr-2"
                    />
                    <label htmlFor="batchIsActive" className="text-sm text-slate-700">
                      Set as Active
                    </label>
                  </div>
                </div>

                <div className="bg-stone-50 p-3 rounded-md">
                  <p className="text-sm text-slate-600 mb-2">
                    <strong>Selected Templates:</strong>
                  </p>
                  <div className="text-xs text-stone-500 max-h-20 overflow-y-auto">
                    {selectedTemplates.map(templateId => {
                      const template = templates.find(t => t.id === templateId);
                      return (
                        <div key={templateId} className="flex justify-between">
                          <span>{template?.title || `Template ${templateId}`}</span>
                          <span className="text-stone-400">#{template?.id}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowBatchModal(false);
                      setBatchUpdateData({});
                    }}
                    className="px-4 py-2 text-slate-600 border border-stone-300 rounded-md hover:bg-stone-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-azure-600 text-white rounded-md hover:bg-azure-700"
                  >
                    Update {selectedTemplates.length} Template{selectedTemplates.length !== 1 ? 's' : ''}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Workflow Editor Modal */}
      {showWorkflowEditor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-slate-900">Manage Workflows & Sub-workflows</h3>
                <button
                  onClick={() => setShowWorkflowEditor(false)}
                  className="text-stone-400 hover:text-slate-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Workflows are currently defined in code. To add/edit workflows, you need to modify the
                  <code className="bg-blue-100 px-1 rounded mx-1">workflowStructure</code> object in the component.
                  This UI shows you what's currently configured.
                </p>
              </div>

              <div className="space-y-6">
                {Object.entries(workflowStructure).map(([workflowKey, workflow]) => (
                  <div key={workflowKey} className="border border-stone-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-lg text-slate-900">{workflow.label}</h4>
                        <p className="text-sm text-stone-500">Key: <code className="bg-stone-100 px-1 rounded">{workflowKey}</code></p>
                      </div>
                      <div className="text-sm text-stone-600">
                        {Object.keys(workflow.subWorkflows).length} sub-workflows
                      </div>
                    </div>

                    <div className="ml-4 space-y-2">
                      <p className="text-sm font-medium text-stone-700 mb-2">Sub-workflows:</p>
                      {Object.entries(workflow.subWorkflows).map(([subKey, subLabel]) => (
                        <div key={subKey} className="flex items-center justify-between p-2 bg-stone-50 rounded">
                          <div>
                            <span className="font-medium text-slate-900">{subLabel}</span>
                            <span className="text-xs text-stone-500 ml-2">
                              Key: <code className="bg-stone-100 px-1 rounded">{subKey}</code>
                            </span>
                          </div>
                          <div className="text-xs text-stone-600">
                            {filteredTemplates.filter(t => t.workflow_key === workflowKey && t.sub_workflow_key === subKey).length} tasks
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-4 border-t">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="font-semibold text-amber-900 mb-2">Want to add a new workflow?</h4>
                  <p className="text-sm text-amber-800 mb-3">
                    To add a new workflow or sub-workflow, you'll need to:
                  </p>
                  <ol className="text-sm text-amber-800 space-y-1 ml-4 list-decimal">
                    <li>Open <code className="bg-amber-100 px-1 rounded">MilestoneTemplateAdmin.jsx</code></li>
                    <li>Find the <code className="bg-amber-100 px-1 rounded">workflowStructure</code> object (around line 714)</li>
                    <li>Add your new workflow/sub-workflow following the existing pattern</li>
                    <li>Save and reload - it will appear in the dropdowns automatically</li>
                  </ol>
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowWorkflowEditor(false)}
                  className="px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Completion Rule Creator Modal */}
      {showCompletionRuleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-slate-900">⚡ Define Completion Rule</h3>
                <button
                  onClick={() => setShowCompletionRuleModal(false)}
                  className="text-stone-400 hover:text-slate-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="bg-azure-50 border border-azure-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-azure-800">
                  Define when this task should auto-complete based on changes in your database.
                  Specify the table, row, column to watch, and the trigger condition.
                </p>
              </div>

              <div className="space-y-4">
                {/* Rule Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Rule Name *
                    <span className="text-xs text-stone-500 ml-1">(unique identifier)</span>
                  </label>
                  <input
                    type="text"
                    value={completionRuleData.rule_name}
                    onChange={(e) => setCompletionRuleData({...completionRuleData, rule_name: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="e.g., finalize_reach_schools"
                  />
                </div>

                {/* Watch Table */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Watch Table *
                    <span className="text-xs text-stone-500 ml-1">(which table to monitor)</span>
                  </label>
                  <select
                    value={completionRuleData.watch_table}
                    onChange={(e) => setCompletionRuleData({...completionRuleData, watch_table: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="timeline_items">timeline_items (Tasks)</option>
                    <option value="student_profiles">student_profiles (Profile Data)</option>
                    <option value="college_selections">college_selections (College List)</option>
                    <option value="essays">essays (Essay Progress)</option>
                    <option value="custom">Custom table...</option>
                  </select>
                </div>

                {/* Watch Row */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Watch Row
                    <span className="text-xs text-stone-500 ml-1">(which specific row/record)</span>
                  </label>
                  {completionRuleData.watch_table === 'timeline_items' ? (
                    <select
                      value={completionRuleData.watch_row_id}
                      onChange={(e) => {
                        const selectedTemplate = templates.find(t => t.id === e.target.value);
                        setCompletionRuleData({
                          ...completionRuleData,
                          watch_row_id: e.target.value,
                          watch_row_label: selectedTemplate?.title || ''
                        });
                      }}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      <option value="">Select a task template...</option>
                      {templates.map(template => (
                        <option key={template.id} value={template.id}>
                          {template.title} ({template.category})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={completionRuleData.watch_row_id}
                      onChange={(e) => setCompletionRuleData({...completionRuleData, watch_row_id: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="Row UUID or identifier"
                    />
                  )}
                </div>

                {/* Watch Column */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Watch Column *
                    <span className="text-xs text-stone-500 ml-1">(which field to monitor)</span>
                  </label>
                  <input
                    type="text"
                    value={completionRuleData.watch_column}
                    onChange={(e) => setCompletionRuleData({...completionRuleData, watch_column: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="e.g., completion_status, is_completed, selected_count"
                    list="common-columns"
                  />
                  <datalist id="common-columns">
                    <option value="completion_status" />
                    <option value="is_completed" />
                    <option value="selected_count" />
                    <option value="tier" />
                    <option value="application_round" />
                  </datalist>
                </div>

                {/* Operator and Value */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Operator *
                    </label>
                    <select
                      value={completionRuleData.operator}
                      onChange={(e) => setCompletionRuleData({...completionRuleData, operator: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      <option value="=">=</option>
                      <option value="!=">!=</option>
                      <option value=">">&gt;</option>
                      <option value="<">&lt;</option>
                      <option value=">=">&gt;=</option>
                      <option value="<=">&lt;=</option>
                      <option value="IS NULL">IS NULL</option>
                      <option value="IS NOT NULL">IS NOT NULL</option>
                      <option value="CONTAINS">CONTAINS</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Value
                      <span className="text-xs text-stone-500 ml-1">(target value)</span>
                    </label>
                    <input
                      type="text"
                      value={completionRuleData.value}
                      onChange={(e) => setCompletionRuleData({...completionRuleData, value: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="e.g., complete, 4, true"
                      disabled={completionRuleData.operator === 'IS NULL' || completionRuleData.operator === 'IS NOT NULL'}
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Description
                    <span className="text-xs text-stone-500 ml-1">(explain when this triggers)</span>
                  </label>
                  <textarea
                    value={completionRuleData.description}
                    onChange={(e) => setCompletionRuleData({...completionRuleData, description: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                    rows="2"
                    placeholder="Auto-complete when reach schools are finalized"
                  />
                </div>

                {/* Preview */}
                {completionRuleData.watch_table && completionRuleData.watch_column && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <h4 className="font-semibold text-emerald-900 mb-2">Preview</h4>
                    <p className="text-sm text-emerald-800">
                      When <code className="bg-emerald-100 px-1 rounded">{completionRuleData.watch_table}</code> row
                      {completionRuleData.watch_row_label && <> "<strong>{completionRuleData.watch_row_label}</strong>"</>}
                      {!completionRuleData.watch_row_label && completionRuleData.watch_row_id && <> <code className="bg-emerald-100 px-1 rounded">{completionRuleData.watch_row_id}</code></>} has{' '}
                      <code className="bg-emerald-100 px-1 rounded">{completionRuleData.watch_column}</code> {completionRuleData.operator} {completionRuleData.value && <code className="bg-emerald-100 px-1 rounded">{completionRuleData.value}</code>},
                      this task will auto-complete.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                <button
                  onClick={() => setShowCompletionRuleModal(false)}
                  className="px-4 py-2 text-slate-600 border border-stone-300 rounded-lg hover:bg-stone-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    // Store the rule in formData.metadata
                    const metadata = formData.metadata || {};
                    metadata.completion_rule = completionRuleData;

                    setFormData({
                      ...formData,
                      completion_rule: completionRuleData.rule_name,
                      metadata: metadata
                    });
                    setShowCompletionRuleModal(false);
                  }}
                  className="px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 transition-colors"
                  disabled={!completionRuleData.rule_name || !completionRuleData.watch_table || !completionRuleData.watch_column}
                >
                  Save Rule
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}